import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { BannersSettings } from '../components/BannersSettings';
import { YeastarSettings } from '../components/YeastarSettings';
import { WhatsAppConnect } from '../components/WhatsAppConnect';
import { useAuth } from '../contexts/AuthContext';
import { emailTemplatesAPI } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  Mail,
  User,
  Calendar,
  Shield,
  Bell,
  Sun,
  AlertCircle,
  Edit,
  Save,
  Loader2,
  Receipt,
  MessagesSquare,
  RotateCcw,
  Code,
  FileText,
  ExternalLink,
  CheckCircle,
  Check,
  Eye,
  Facebook,
  Lock,
  KeyRound,
  AtSign,
  GitBranch,
  ChevronUp,
  ChevronDown,
  ClipboardCheck,
  Plus,
  Trash2,
  ShieldCheck,
  Phone,
  Send,
  Cloud,
  Image,
  Copy,
  Upload,
  Link,
  RefreshCw,
  Activity,
  Zap,
  Globe,
  Key,
  Package,
  Users,
} from 'lucide-react';
import { formatDate, STATUS_DISPLAY_NAMES } from '../lib/utils';
import { toast } from 'sonner';
import { EmailModeToggle } from '../components/EmailModeToggle';
import { EmailPreviewDialog } from '../components/EmailPreviewDialog';
import { resolveBodyHtml } from '../lib/emailEditor';

const EMAIL_TRIGGERS = [
  { value: 'new_portal_account', label: 'Portal Welcome Email', description: 'Sent when a new customer portal account is created' },
  { value: 'portal_password_reset', label: 'Portal Password Reset', description: 'Sent when a customer requests a portal password reset' },
  { value: 'staff_password_reset', label: 'Staff Password Reset', description: 'Sent when a staff member requests a password reset' },
  { value: 'deposit_taken', label: 'Deposit Taken', description: 'Sent when customer deposit is received' },
  { value: 'scheduled', label: 'Scheduled', description: 'Sent when installation is scheduled' },
  { value: 'ordered', label: 'Ordered', description: 'Sent when equipment is ordered' },
  { value: 'pre_installation', label: 'Pre-Installation', description: 'Sent before installation day with preparation info' },
  { value: 'in_progress_installation', label: 'In Progress (Installation)', description: 'Sent when installation begins' },
  { value: 'in_progress_invoiced', label: 'Invoiced', description: 'Sent when invoice is generated' },
  { value: 'in_progress_handover', label: 'Handover', description: 'Sent during handover preparation' },
  { value: 'completed', label: 'Completed', description: 'Sent when project is complete' },
  { value: 'password_reset', label: 'Password Reset', description: 'Sent when password reset is requested' },
  { value: 'installation_reminder', label: 'Installation Reminder', description: 'Sent 5 days before confirmed installation' },
  // Contract pack lifecycle — every auto-email the CRM sends for a contract
  // pack is editable here. Variables: {{sign_link}}, {{download_link}},
  // {{admin_link}}, {{token_ttl_days}}, {{pack_type}}, {{full_name}}.
  { value: 'contract_pack_sent', label: 'Contract Pack — Customer Sign Link', description: 'First email sent to the customer with the signing link' },
  { value: 'contract_pack_reminder', label: 'Contract Pack — Sign Reminder', description: 'Manual or scheduled reminder to a customer who hasn\'t signed yet' },
  { value: 'contract_pack_signed_admin_notify', label: 'Contract Pack — Customer Signed (Admin Alert)', description: 'Internal email to admins when a customer signs, asking them to counter-sign' },
  { value: 'contract_pack_fully_executed', label: 'Contract Pack — Fully Executed Copy to Customer', description: 'Sent to the customer after admin counter-signs, with the download link' },
  { value: 'variation_sign_link', label: 'Variation of Contract — Sign Link', description: 'Sent to the customer when a Variation of Contract is raised' },
  { value: 'variation_sign_reminder', label: 'Variation of Contract — Sign Reminder', description: 'Reminder for an unsigned Variation of Contract' },
  { value: 'deposit_contract_next_steps', label: 'Deposit Paid + Contract Signed — What Happens Next', description: 'Auto-sent once to NEW customers when both their deposit is paid AND their contract is signed, explaining the next steps' },
];

const CONTRACT_TEMPLATE_VARIABLES = [
  '{{sign_link}}', '{{download_link}}', '{{admin_link}}', '{{token_ttl_days}}',
  '{{pack_type}}', '{{full_name}}', '{{company_name}}',
];

const TEMPLATE_VARIABLES = [
  '{{first_name}}', '{{last_name}}', '{{name}}', '{{email}}', '{{phone}}',
  '{{address}}', '{{city}}', '{{postcode}}',
  '{{system_size}}', '{{system_total_cost}}', '{{deposit_amount}}', '{{final_bill_amount}}',
  '{{provisional_install_date}}', '{{confirmed_install_date}}', '{{installation_type}}', '{{installation_days}}',
  '{{panel_manufacturer}}', '{{inverter_manufacturer}}', '{{battery_manufacturer}}',
  '{{panel_quantity}}', '{{monthly_bill}}', '{{roof_type}}', '{{status}}',
  '{{setup_url}}', '{{reset_link}}', '{{portal_url}}',
  // Contract pack lifecycle variables
  '{{sign_link}}', '{{download_link}}', '{{admin_link}}', '{{token_ttl_days}}',
  '{{pack_type}}', '{{full_name}}', '{{company_name}}'
];

// Sample data for preview
const SAMPLE_CUSTOMER = {
  first_name: 'John',
  last_name: 'Smith',
  name: 'John Smith',
  email: 'john.smith@example.com',
  address: '123 Solar Street, London',
  city: 'London',
  postcode: 'SW1A 1AA',
  phone: '07700 900123',
  system_size: '6.6',
  system_total_cost: '12500',
  deposit_amount: '5000',
  final_bill_amount: '7500',
  provisional_install_date: '2026-05-15',
  confirmed_install_date: '2026-05-20',
  installation_type: 'Solar PV',
  installation_days: '2',
  panel_manufacturer: 'JA Solar',
  inverter_manufacturer: 'Fox ESS',
  battery_manufacturer: 'Fox ESS',
  panel_quantity: '12',
  monthly_bill: '150',
  roof_type: 'Pitched',
  status: 'In Progress',
  portal_url: 'https://app.helioses.co.uk/portal/login',
  setup_url: 'https://app.helioses.co.uk/portal/setup-password?token=example',
  reset_link: 'https://app.helioses.co.uk/portal/setup-password?token=example',
};

// ─── Wix / Website Leads Settings Component ───
const WixLeadsSettings = () => {
  const [settings, setSettings] = useState({ api_key: '', site_id: '', account_id: '', enabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    api.get('/admin/wix-leads/settings')
      .then(res => setSettings(res.data))
      .catch(() => toast.error('Failed to load Wix settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/admin/wix-leads/settings', settings);
      toast.success('Wix settings saved');
      setTestResult(null);
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/admin/wix-leads/test');
      setTestResult(res.data);
    } catch {
      setTestResult({ status: 'error', message: 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200">
      <CardHeader>
        <div className="space-y-1">
          <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
            <Globe className="w-5 h-5 text-teal-600" />
            Wix Website Leads
          </CardTitle>
          <CardDescription>
            Connect to your Wix website to auto-sync form submissions as leads.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4">
          <div className="space-y-2">
            <Label htmlFor="wix-api-key" className="text-sm font-medium">API Key</Label>
            <Input
              id="wix-api-key"
              type="password"
              placeholder="IST.eyJ..."
              value={settings.api_key}
              onChange={(e) => setSettings({ ...settings, api_key: e.target.value })}
              data-testid="wix-api-key-input"
            />
            <p className="text-xs text-slate-500">
              Generate from Wix Dashboard &rarr; Settings &rarr; API Keys. Requires &quot;Wix Forms: Read Submissions&quot; permission.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="wix-site-id" className="text-sm font-medium">Site ID</Label>
              <Input
                id="wix-site-id"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={settings.site_id}
                onChange={(e) => setSettings({ ...settings, site_id: e.target.value })}
                data-testid="wix-site-id-input"
              />
              <p className="text-xs text-slate-500">UUID from your Wix site URL in the editor.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="wix-account-id" className="text-sm font-medium">Account ID</Label>
              <Input
                id="wix-account-id"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={settings.account_id}
                onChange={(e) => setSettings({ ...settings, account_id: e.target.value })}
                data-testid="wix-account-id-input"
              />
              <p className="text-xs text-slate-500">Your Wix business account ID.</p>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              data-testid="wix-enabled-switch"
            />
            <Label className="text-sm">Enable auto-sync (every 15 minutes)</Label>
          </div>
        </div>

        {testResult && (
          <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
            testResult.status === 'connected'
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`} data-testid="wix-test-result">
            {testResult.status === 'connected' ? (
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
            )}
            {testResult.message}
          </div>
        )}

        <div className="flex items-center gap-3 pt-2">
          <Button onClick={handleSave} disabled={saving} data-testid="wix-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Settings
          </Button>
          <Button variant="outline" onClick={handleTest} disabled={testing} data-testid="wix-test-btn">
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            Test Connection
          </Button>
        </div>

        {settings.last_sync && (
          <p className="text-xs text-slate-500">
            Last sync: {formatDate(settings.last_sync)}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Microsoft 365 Settings Component ───
const MS365Settings = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [calendarRecipients, setCalendarRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState('');
  const [savingRecipients, setSavingRecipients] = useState(false);
  const [bookingTest, setBookingTest] = useState(null);
  const [bookingTesting, setBookingTesting] = useState(false);

  const runBookingSelftest = async () => {
    setBookingTesting(true);
    setBookingTest(null);
    try {
      const res = await api.post('/website-form/booking-selftest', { to: testEmail || undefined });
      setBookingTest(res.data);
      if (res.data?.calendar?.ok && res.data?.email?.ok) {
        toast.success('Booking pipeline healthy — calendar invite + email sent from hello@');
      } else {
        toast.error('Booking pipeline has a problem — see details below');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Self-test failed');
    } finally { setBookingTesting(false); }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statusRes, recipientsRes] = await Promise.all([
          api.get('/settings/ms365/status'),
          api.get('/settings/ms365/calendar-recipients'),
        ]);
        setStatus(statusRes.data);
        setCalendarRecipients(recipientsRes.data.recipients || []);
      } catch { setStatus({ connected: false, error: 'Failed to fetch status' }); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const handleTestEmail = async () => {
    if (!testEmail) return;
    setSending(true);
    try {
      await api.post('/settings/ms365/test-email', { to_email: testEmail });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test email');
    } finally { setSending(false); }
  };

  const addRecipient = () => {
    const email = newRecipient.trim().toLowerCase();
    if (email && email.includes('@') && !calendarRecipients.includes(email)) {
      setCalendarRecipients(prev => [...prev, email]);
      setNewRecipient('');
    }
  };

  const removeRecipient = (email) => {
    setCalendarRecipients(prev => prev.filter(r => r !== email));
  };

  const saveRecipients = async () => {
    setSavingRecipients(true);
    try {
      await api.post('/settings/ms365/calendar-recipients', { recipients: calendarRecipients });
      toast.success('Calendar recipients saved');
    } catch (e) {
      toast.error('Failed to save recipients');
    } finally { setSavingRecipients(false); }
  };

  const [refreshingInvites, setRefreshingInvites] = useState(false);
  const refreshFutureInvites = async () => {
    setRefreshingInvites(true);
    try {
      const r = await api.post('/settings/calendar/refresh-future-invites');
      const d = r.data || {};
      toast.success(`Refreshed ${d.updated || 0} of ${d.total || 0} future invite(s)${d.failed ? ` · ${d.failed} failed` : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to refresh invites');
    } finally { setRefreshingInvites(false); }
  };

  // ── Contacts sync to a shared MS365 address book (shows names on phones) ──
  const [contactsStatus, setContactsStatus] = useState(null);
  const [contactsMailbox, setContactsMailbox] = useState('');
  const [savingContacts, setSavingContacts] = useState(false);
  const [syncingContacts, setSyncingContacts] = useState(false);

  const loadContactsStatus = async () => {
    try {
      const r = await api.get('/ms365-contacts/status');
      setContactsStatus(r.data);
      setContactsMailbox(r.data.mailbox || '');
    } catch { /* admin-only; ignore */ }
  };
  useEffect(() => { loadContactsStatus(); }, []);

  const saveContactsConfig = async () => {
    setSavingContacts(true);
    try {
      await api.post('/ms365-contacts/config', { mailbox: contactsMailbox });
      toast.success('Contact folder ready in Microsoft 365');
      await loadContactsStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not set up the contact folder');
    } finally { setSavingContacts(false); }
  };

  const syncAllContacts = async () => {
    setSyncingContacts(true);
    try {
      const r = await api.post('/ms365-contacts/sync-all');
      toast.success(r.data?.message || 'Contact sync started');
      setTimeout(loadContactsStatus, 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start the sync');
    } finally { setSyncingContacts(false); }
  };

  const toggleNightlySync = async (enabled) => {
    // Optimistic update
    setContactsStatus((s) => s ? { ...s, nightly_sync_enabled: enabled } : s);
    try {
      await api.post('/ms365-contacts/nightly', { enabled });
      toast.success(enabled ? 'Nightly contact sync turned on' : 'Nightly contact sync turned off');
    } catch (e) {
      setContactsStatus((s) => s ? { ...s, nightly_sync_enabled: !enabled } : s);
      toast.error('Could not update nightly sync');
    }
  };

  return (
    <Card className="border-slate-200" data-testid="ms365-settings">
      <CardHeader>
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Cloud className="w-5 h-5 text-blue-500" />
          Microsoft 365 Integration
        </CardTitle>
        <CardDescription>
          Send emails via Outlook, sync calendars, and access OneDrive/SharePoint.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Connection Status */}
        <div className="rounded-lg border p-4 space-y-3" data-testid="ms365-connection-status">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Connection Status</Label>
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            ) : status?.connected ? (
              <Badge className="bg-green-100 text-green-700 border-green-200">Connected</Badge>
            ) : status?.configured ? (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200">Configured - Check Permissions</Badge>
            ) : (
              <Badge variant="outline" className="text-slate-500">Not Configured</Badge>
            )}
          </div>
          {!loading && status?.connected && (
            <div className="text-sm text-slate-600 space-y-1">
              <p><span className="font-medium">Display Name:</span> {status.display_name}</p>
              <p><span className="font-medium">Mailbox:</span> {status.email}</p>
            </div>
          )}
          {!loading && !status?.connected && status?.configured && (
            <div className="text-sm text-amber-700 bg-amber-50 rounded p-3">
              <p className="font-medium">Connection issue:</p>
              <p>{status.error}</p>
              <p className="mt-2 text-xs text-amber-600">
                Make sure the Azure App has Mail.Send, Calendars.ReadWrite, and User.Read.All permissions granted with admin consent, and that the mailbox exists.
              </p>
            </div>
          )}
          {!loading && status?.sender_email && (
            <p className="text-sm text-slate-500"><span className="font-medium">Sending as:</span> {status.sender_email}</p>
          )}
        </div>

        {/* Send Test Email */}
        {status?.configured && (
          <div className="rounded-lg border p-4 space-y-3" data-testid="ms365-test-email">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Send className="w-4 h-4" />
              Send Test Email
            </Label>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="recipient@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="flex-1"
                data-testid="ms365-test-email-input"
              />
              <Button onClick={handleTestEmail} disabled={sending || !testEmail} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="ms365-send-test-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                Send
              </Button>
            </div>
          </div>
        )}

        {/* Book-a-call delivery self-test */}
        {status?.configured && (
          <div className="rounded-lg border p-4 space-y-3" data-testid="ms365-booking-selftest">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Test Book-a-Call Delivery
            </Label>
            <p className="text-xs text-slate-500">
              Sends a real calendar invite and email <strong>from hello@helioses.co.uk</strong> to the address above
              (or your own) so you can confirm website bookings reach Outlook. Safe to delete the test event/email.
            </p>
            <Button onClick={runBookingSelftest} disabled={bookingTesting} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="ms365-booking-selftest-btn">
              {bookingTesting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Calendar className="w-4 h-4 mr-1" />}
              Run booking self-test
            </Button>
            {bookingTest && (
              <div className="text-sm space-y-1 mt-2" data-testid="ms365-booking-selftest-result">
                <p>Sender mailbox: <strong>{bookingTest.sender}</strong></p>
                <p className={bookingTest.calendar?.ok ? 'text-emerald-600' : 'text-red-600'}>
                  Calendar invite: {bookingTest.calendar?.ok ? '✓ sent' : `✗ failed`}
                </p>
                {bookingTest.calendar?.error && <p className="text-xs text-red-500 break-words">{bookingTest.calendar.error}</p>}
                <p className={bookingTest.email?.ok ? 'text-emerald-600' : 'text-red-600'}>
                  Email: {bookingTest.email?.ok ? '✓ sent' : '✗ failed'}
                </p>
                {bookingTest.email?.error && <p className="text-xs text-red-500 break-words">{bookingTest.email.error}</p>}
                {(!bookingTest.calendar?.ok || !bookingTest.email?.ok) && (
                  <p className="text-xs text-amber-600 mt-1">
                    Tip: check the Azure app has <strong>Mail.Send</strong> + <strong>Calendars.ReadWrite</strong> (admin-consented)
                    and can send as hello@helioses.co.uk, and that MS365_TENANT_ID is your real tenant.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        <div className="rounded-lg border p-4 space-y-3" data-testid="ms365-calendar-recipients">
          <Label className="text-sm font-medium flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Calendar Invitation Recipients
          </Label>
          <p className="text-xs text-slate-500">
            When a new installation is scheduled, calendar invitations are automatically sent to these email addresses.
          </p>
          <div className="space-y-2">
            {calendarRecipients.map((email) => (
              <div key={email} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2 group" data-testid={`calendar-recipient-${email}`}>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-500" />
                  <span className="text-sm">{email}</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => removeRecipient(email)}
                  data-testid={`remove-recipient-${email}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="Add email address..."
              value={newRecipient}
              onChange={(e) => setNewRecipient(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addRecipient()}
              className="flex-1"
              data-testid="ms365-new-recipient-input"
            />
            <Button
              variant="outline"
              onClick={addRecipient}
              disabled={!newRecipient.includes('@')}
              data-testid="ms365-add-recipient-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
          <Button
            onClick={saveRecipients}
            disabled={savingRecipients}
            className="bg-blue-600 hover:bg-blue-700 text-white w-full"
            data-testid="ms365-save-recipients-btn"
          >
            {savingRecipients ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Recipients
          </Button>
          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-500 mb-2">
              Re-send the latest equipment (make/model/qty) and customer photos to all
              <strong> upcoming</strong> installation invites already in the calendar.
            </p>
            <Button
              variant="outline"
              onClick={refreshFutureInvites}
              disabled={refreshingInvites}
              className="w-full"
              data-testid="refresh-future-invites-btn"
            >
              {refreshingInvites ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
              Refresh all future invites
            </Button>
          </div>

          {/* Contacts sync — customer names on all phones */}
          <div className="pt-3 mt-1 border-t border-slate-100 space-y-3" data-testid="ms365-contacts-sync">
            <div>
              <Label className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-600" /> Sync customers to phone contacts
              </Label>
              <p className="text-xs text-slate-500 mt-1">
                Pushes every customer into a shared Microsoft 365 contact folder. Any phone signed into that
                mailbox then shows the customer's name on WhatsApp and calls automatically.
                <span className="block mt-1 text-amber-600">Requires the <strong>Contacts.ReadWrite</strong> application permission (with admin consent) on your Azure app.</span>
              </p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Shared mailbox for contacts</Label>
              <Input
                value={contactsMailbox}
                onChange={(e) => setContactsMailbox(e.target.value)}
                placeholder="e.g. contacts@helioses.co.uk"
                data-testid="ms365-contacts-mailbox-input"
              />
            </div>
            {contactsStatus && (
              <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1" data-testid="ms365-contacts-status">
                <span>Folder: <strong>{contactsStatus.folder_name}</strong> {contactsStatus.configured ? '✓ ready' : '(not created yet)'}</span>
                <span>Synced: <strong>{contactsStatus.synced_contacts}</strong> / {contactsStatus.total_contacts}</span>
                {contactsStatus.last_sync_at && <span>Last sync: {new Date(contactsStatus.last_sync_at).toLocaleString('en-GB')}</span>}
                {contactsStatus.last_sync_result?.error && <span className="text-rose-600">Last error: {contactsStatus.last_sync_result.error}</span>}
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={saveContactsConfig} disabled={savingContacts || !contactsMailbox} className="flex-1" data-testid="ms365-contacts-save-btn">
                {savingContacts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Create / update folder
              </Button>
              <Button onClick={syncAllContacts} disabled={syncingContacts} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" data-testid="ms365-contacts-sync-btn">
                {syncingContacts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                Sync all customers now
              </Button>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div>
                <p className="text-sm font-medium text-slate-700">Automatic nightly sync</p>
                <p className="text-xs text-slate-500">Runs every night (~01:00 UTC) so new & renamed customers reach the phones automatically.</p>
              </div>
              <Switch
                checked={contactsStatus?.nightly_sync_enabled ?? true}
                onCheckedChange={toggleNightlySync}
                data-testid="ms365-contacts-nightly-toggle"
              />
            </div>
          </div>
        </div>

        {/* Integration Features */}
        <div className="space-y-3">
          <Label className="text-sm font-medium">Enabled Features</Label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="rounded-lg border p-3 flex items-start gap-3">
              <Mail className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium">Outlook Email</p>
                <p className="text-xs text-slate-500">All emails sent via MS365</p>
              </div>
              {status?.configured && <CheckCircle className="w-4 h-4 text-green-500 ml-auto mt-0.5" />}
            </div>
            <div className="rounded-lg border p-3 flex items-start gap-3">
              <Calendar className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium">Calendar Sync</p>
                <p className="text-xs text-slate-500">Auto-invite on new installs</p>
              </div>
              {status?.configured && <CheckCircle className="w-4 h-4 text-green-500 ml-auto mt-0.5" />}
            </div>
            <div className="rounded-lg border p-3 flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium">SharePoint</p>
                <p className="text-xs text-slate-500">Customer Handover Packs</p>
              </div>
              {status?.configured && <CheckCircle className="w-4 h-4 text-green-500 ml-auto mt-0.5" />}
            </div>
          </div>
        </div>

        {/* Configuration Info */}
        <div className="rounded-lg bg-slate-50 p-4 space-y-2">
          <p className="text-sm font-medium text-slate-700">Configuration</p>
          <p className="text-xs text-slate-500">
            Microsoft 365 credentials are configured via environment variables on the server.
            Contact your system administrator to update Client ID, Tenant ID, Client Secret, or the sending mailbox.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export const Settings = () => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, template: null });
  const [previewDialog, setPreviewDialog] = useState({ open: false, template: null });
  const [formData, setFormData] = useState({ subject: '', body_html: '' });
  const [saving, setSaving] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [newTemplateData, setNewTemplateData] = useState({ trigger: '', label: '', subject: '', body_html: '' });
  
  // Send email from template
  const [sendDialog, setSendDialog] = useState({ open: false, template: null });
  const [sendCustomerId, setSendCustomerId] = useState('');
  const [sendSubject, setSendSubject] = useState('');
  const [sendBody, setSendBody] = useState('');
  const [customersList, setCustomersList] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendEditorMode, setSendEditorMode] = useState('html');
  const [sendPreviewOpen, setSendPreviewOpen] = useState(false);
  // Editor mode used in the template editor itself
  const [templateEditorMode, setTemplateEditorMode] = useState('html');
  const [templatePreviewOpen, setTemplatePreviewOpen] = useState(false);
  
  // Xero settings (OAuth)
  const [xeroStatus, setXeroStatus] = useState({ connected: false, tenant_name: '' });
  const [xeroLoading, setXeroLoading] = useState(false);
  const [xeroSyncing, setXeroSyncing] = useState(false);
  const [xeroApiSettings, setXeroApiSettings] = useState({ client_id: '', client_secret_set: false, redirect_uri: '', scopes: '' });
  const [xeroApiSaving, setXeroApiSaving] = useState(false);
  const [xeroClientSecret, setXeroClientSecret] = useState('');
  const [xeroBankAccounts, setXeroBankAccounts] = useState([]);
  const [invoicingConfig, setInvoicingConfig] = useState({ invoicing_mode: 'two_invoice', deposit_percent: 25 });
  const [invoicingSaving, setInvoicingSaving] = useState(false);
  const [chatConfig, setChatConfig] = useState({ office_hours: {}, canned_replies: [] });
  const [chatConfigSaving, setChatConfigSaving] = useState(false);

  // Messaging settings
  const [msgSettings, setMsgSettings] = useState({ page_access_token: '', page_id: '', webhook_verify_token: '', instagram_account_id: '' });
  const [msgTokenInput, setMsgTokenInput] = useState('');
  const [msgSaving, setMsgSaving] = useState(false);
  const [msgSyncing, setMsgSyncing] = useState(false);

  // Instagram settings
  const [igSettings, setIgSettings] = useState({ ig_app_id: '', ig_app_secret_set: false, ig_access_token_set: false, ig_account_id: '', ig_webhook_verify_token: '' });
  const [igAppSecret, setIgAppSecret] = useState('');
  const [igAccessToken, setIgAccessToken] = useState('');
  const [igSaving, setIgSaving] = useState(false);
  const [igSyncing, setIgSyncing] = useState(false);

  // SharePoint settings
  const [sharepointSettings, setSharepointSettings] = useState({
    site_url: '',
    client_id: '',
    client_secret: '',
    tenant_id: '',
    document_library: 'Documents',
    enabled: false,
  });
  const [sharepointLoading, setSharepointLoading] = useState(false);
  const [sharepointSaving, setSharepointSaving] = useState(false);
  const [backupAllRunning, setBackupAllRunning] = useState(false);
  const [backupAllResult, setBackupAllResult] = useState(null);
  const [dedupeRunning, setDedupeRunning] = useState(false);
  const [dedupeResult, setDedupeResult] = useState(null);
  const [healthRunning, setHealthRunning] = useState(false);
  const [healthResult, setHealthResult] = useState(null);
  const healthRepairInputRef = React.useRef(null);
  const [healthRepairTarget, setHealthRepairTarget] = useState(null);
  const [healthRepairingId, setHealthRepairingId] = useState(null);

  // Meta Leads settings
  const [metaSettings, setMetaSettings] = useState({
    app_id: '',
    app_secret: '',
    access_token: '',
    page_id: '',
    form_id: '',
    webhook_verify_token: '',
    enabled: false,
  });
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaSaving, setMetaSaving] = useState(false);

  // Meta Leads access control
  const [metaAccessUsers, setMetaAccessUsers] = useState([]);
  const [metaAllowedIds, setMetaAllowedIds] = useState([]);
  const [metaAccessLoading, setMetaAccessLoading] = useState(false);
  const [metaAccessSaving, setMetaAccessSaving] = useState(false);

  // Password change
  const [passwordData, setPasswordData] = useState({ current: '', new_password: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);

  // Monitoring settings
  const [monitoringSettings, setMonitoringSettings] = useState({
    foxess_api_key: '', foxess_client_id: '', foxess_client_secret: '', foxess_redirect_uri: '', growatt_api_token: '',
    solis_key_id: '', solis_key_secret: '', solis_api_url: '',
    tesla_client_id: '', tesla_client_secret: '', tesla_redirect_uri: '',
  });
  const [monitoringLoading, setMonitoringLoading] = useState(false);
  const [monitoringSaving, setMonitoringSaving] = useState(false);

  // Email config
  const [emailConfig, setEmailConfig] = useState({
    smtp_host: 'smtp.office365.com',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    use_tls: true,
    mention_emails_enabled: true,
  });
  const [emailConfigLoading, setEmailConfigLoading] = useState(false);
  const [emailConfigSaving, setEmailConfigSaving] = useState(false);
  const [emailBranding, setEmailBranding] = useState({
    company_name: '', registration_line: '', registered_office: '', phone: '', website: '',
  });
  const [emailBrandingSaving, setEmailBrandingSaving] = useState(false);

  // Process flow - status notification config
  const [statusNotifConfig, setStatusNotifConfig] = useState({});
  const [statusNotifLoading, setStatusNotifLoading] = useState(false);
  const [statusNotifSaving, setStatusNotifSaving] = useState(false);
  const [allStaff, setAllStaff] = useState([]);

  // Checklist template
  const [templateItems, setTemplateItems] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [newTemplateItem, setNewTemplateItem] = useState('');

  // 3CX phone config
  const [phoneConfig, setPhoneConfig] = useState([]);
  const [phoneConfigLoading, setPhoneConfigLoading] = useState(false);
  const [phoneConfigSaving, setPhoneConfigSaving] = useState(false);

  // Email images
  const [emailImages, setEmailImages] = useState([]);
  const [emailImagesLoading, setEmailImagesLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [newImageSlug, setNewImageSlug] = useState('');
  const [newImageFile, setNewImageFile] = useState(null);
  const imageFileRef = React.useRef(null);
  const BASE_URL = '';

  const STAGES = [
    { key: 'lead', label: 'Lead', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    { key: 'qualified', label: 'Qualified', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
    { key: 'quote_sent', label: 'Quote Sent', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
    { key: 'approved', label: 'Approved', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    { key: 'deposit_taken', label: 'Deposit Taken', color: 'bg-teal-100 text-teal-700 border-teal-200' },
    { key: 'scheduled', label: 'Scheduled', color: 'bg-lime-100 text-lime-700 border-lime-200' },
    { key: 'ordered', label: 'Ordered', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
    { key: 'in_progress_installation', label: 'In Progress (Installation)', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    { key: 'in_progress_invoiced', label: 'In Progress (Invoiced)', color: 'bg-amber-100 text-amber-700 border-amber-200' },
    { key: 'in_progress_handover', label: 'In Progress (Handover)', color: 'bg-rose-100 text-rose-700 border-rose-200' },
    { key: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700 border-green-200' },
  ];

  const fetchTemplates = async () => {
    try {
      const response = await emailTemplatesAPI.getAll();
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchXeroSettings = async () => {
    setXeroLoading(true);
    try {
      const [statusRes, settingsRes, invCfgRes] = await Promise.all([
        api.get('/xero/status'),
        api.get('/xero/settings').catch(() => ({ data: {} })),
        api.get('/xero/invoicing-config').catch(() => ({ data: null })),
      ]);
      setXeroStatus(statusRes.data || { connected: false });
      if (settingsRes.data) {
        setXeroApiSettings(settingsRes.data);
      }
      if (statusRes.data?.connected) {
        api.get('/xero/bank-accounts').then((r) => setXeroBankAccounts(r.data || [])).catch(() => setXeroBankAccounts([]));
      }
      if (invCfgRes.data) {
        setInvoicingConfig(invCfgRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch Xero status');
    } finally {
      setXeroLoading(false);
    }
  };

  const handleSaveXeroApi = async () => {
    setXeroApiSaving(true);
    try {
      const payload = {
        client_id: xeroApiSettings.client_id,
        client_secret: xeroClientSecret || '',
        redirect_uri: xeroApiSettings.redirect_uri,
        scopes: xeroApiSettings.scopes,
        stripe_bank_account_code: xeroApiSettings.stripe_bank_account_code || '',
      };
      const res = await api.post('/xero/settings', payload);
      toast.success('Xero API settings saved');
      setXeroClientSecret('');
      fetchXeroSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Xero settings');
    } finally {
      setXeroApiSaving(false);
    }
  };

  const handleXeroConnect = async () => {
    try {
      const response = await api.get('/xero/authorize');
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Failed to start Xero connection');
    }
  };

  const handleSaveInvoicingConfig = async (next) => {
    setInvoicingSaving(true);
    try {
      const payload = {
        invoicing_mode: next.invoicing_mode ?? invoicingConfig.invoicing_mode,
        deposit_percent: Number(next.deposit_percent ?? invoicingConfig.deposit_percent) || 25,
        zero_rated_tax_type: invoicingConfig.zero_rated_tax_type || 'ZERORATEDOUTPUT',
        standard_tax_type: invoicingConfig.standard_tax_type || 'OUTPUT2',
      };
      const res = await api.post('/xero/invoicing-config', payload);
      setInvoicingConfig(res.data);
      toast.success('Invoicing settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save invoicing settings');
    } finally {
      setInvoicingSaving(false);
    }
  };

  const handleSaveChatConfig = async () => {
    setChatConfigSaving(true);
    try {
      const res = await api.post('/website-chat/config', {
        office_hours: chatConfig.office_hours,
        canned_replies: (chatConfig.canned_replies || []).filter(c => (c.text || '').trim()),
      });
      setChatConfig(res.data);
      toast.success('Website chat settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save chat settings');
    } finally {
      setChatConfigSaving(false);
    }
  };

  const [xeroDiagnostics, setXeroDiagnostics] = useState(null);
  const [xeroDiagLoading, setXeroDiagLoading] = useState(false);
  const handleXeroDiagnose = async () => {
    setXeroDiagLoading(true);
    try {
      const r = await api.get('/xero/diagnostics');
      setXeroDiagnostics(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Diagnostics failed');
    } finally {
      setXeroDiagLoading(false);
    }
  };

  const handleXeroDisconnect = async () => {
    if (!window.confirm('Disconnect from Xero? This will remove all Xero integration features.')) return;
    try {
      await api.post('/xero/disconnect');
      setXeroStatus({ connected: false });
      toast.success('Xero disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Xero');
    }
  };

  const handleXeroSyncContacts = async () => {
    setXeroSyncing(true);
    try {
      const response = await api.post('/xero/sync-contacts');
      const d = response.data;
      toast.success(`Synced to Xero: ${d.created} created, ${d.updated} updated${d.errors ? `, ${d.errors} errors` : ''}`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Contact sync failed';
      toast.error(msg);
    }
    setXeroSyncing(false);
  };

  const [xeroInvoiceSyncing, setXeroInvoiceSyncing] = useState(false);
  const [xeroDocTemplates, setXeroDocTemplates] = useState([]);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '', doc_type: 'invoice', reference_template: 'Helios - {{name}}', due_days: 30, is_default: false,
    line_items: [{ description: '', quantity: 1, unit_amount: 0, account_code: '200' }]
  });
  const handleXeroSyncInvoices = async () => {
    setXeroInvoiceSyncing(true);
    try {
      const response = await api.post('/xero/sync-invoice-statuses');
      const d = response.data;
      toast.success(`Invoice sync: ${d.synced} updated out of ${d.total_checked} checked`);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Invoice sync failed';
      toast.error(msg);
    }
    setXeroInvoiceSyncing(false);
  };

  const fetchXeroDocTemplates = async () => {
    try {
      const res = await api.get('/xero/doc-templates');
      setXeroDocTemplates(res.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchXeroDocTemplates(); }, []);
  useEffect(() => {
    api.get('/website-chat/config')
      .then(({ data }) => setChatConfig(data))
      .catch(() => {});
  }, []);

  const handleSaveTemplate = async () => {
    if (!templateForm.name?.trim()) { toast.error('Template name is required'); return; }
    try {
      if (editingTemplate) {
        await api.put(`/xero/doc-templates/${editingTemplate.id}`, templateForm);
        toast.success('Template updated');
      } else {
        await api.post('/xero/doc-templates', templateForm);
        toast.success('Template created');
      }
      setShowTemplateForm(false);
      setEditingTemplate(null);
      fetchXeroDocTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await api.delete(`/xero/doc-templates/${id}`);
      toast.success('Template deleted');
      fetchXeroDocTemplates();
    } catch { toast.error('Failed to delete'); }
  };

  const handleEditTemplate = (tmpl) => {
    setEditingTemplate(tmpl);
    setTemplateForm({
      name: tmpl.name, doc_type: tmpl.doc_type, reference_template: tmpl.reference_template || '',
      due_days: tmpl.due_days || 30, is_default: tmpl.is_default || false,
      line_items: tmpl.line_items?.length > 0 ? tmpl.line_items : [{ description: '', quantity: 1, unit_amount: 0, account_code: '200' }],
    });
    setShowTemplateForm(true);
  };

  // Messaging settings handlers
  const fetchMsgSettings = async () => {
    try {
      const res = await api.get('/messaging/settings');
      setMsgSettings(res.data || {});
    } catch { /* ignore */ }
  };
  useEffect(() => { fetchMsgSettings(); }, []);

  const handleSaveMsgSettings = async () => {
    setMsgSaving(true);
    try {
      const payload = {
        page_id: msgSettings.page_id,
        webhook_verify_token: msgSettings.webhook_verify_token,
        instagram_account_id: msgSettings.instagram_account_id || '',
      };
      if (msgTokenInput && msgTokenInput !== '***') {
        payload.page_access_token = msgTokenInput;
      }
      await api.post('/messaging/settings', payload);
      toast.success('Messaging settings saved');
      setMsgTokenInput('');
      fetchMsgSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save messaging settings');
    }
    setMsgSaving(false);
  };

  const handleSyncMessages = async () => {
    setMsgSyncing(true);
    try {
      const res = await api.post('/messaging/sync');
      toast.success(`Synced ${res.data?.synced_conversations || 0} conversations`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    }
    setMsgSyncing(false);
  };

  // Instagram settings handlers
  const fetchIgSettings = async () => {
    try {
      const res = await api.get('/instagram/settings');
      setIgSettings(res.data || {});
    } catch { /* ignore */ }
  };
  useEffect(() => { fetchIgSettings(); }, []);

  const handleSaveIgSettings = async () => {
    setIgSaving(true);
    try {
      const payload = {
        ig_app_id: igSettings.ig_app_id,
        ig_account_id: igSettings.ig_account_id || '',
        ig_webhook_verify_token: igSettings.ig_webhook_verify_token || '',
      };
      if (igAppSecret && igAppSecret !== '***') payload.ig_app_secret = igAppSecret;
      if (igAccessToken && igAccessToken !== '***') payload.ig_access_token = igAccessToken;
      await api.post('/instagram/settings', payload);
      toast.success('Instagram settings saved');
      setIgAppSecret('');
      setIgAccessToken('');
      fetchIgSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Instagram settings');
    }
    setIgSaving(false);
  };

  const handleSyncInstagram = async () => {
    setIgSyncing(true);
    try {
      const res = await api.post('/instagram/sync');
      toast.success(`Synced ${res.data?.synced_conversations || 0} Instagram conversations`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Instagram sync failed');
    }
    setIgSyncing(false);
  };

  const fetchSharepointSettings = async () => {
    setSharepointLoading(true);
    try {
      const response = await api.get('/settings/sharepoint');
      setSharepointSettings({
        site_url: response.data.site_url || '',
        client_id: response.data.client_id || '',
        client_secret: '',
        client_secret_set: response.data.client_secret_set || false,
        tenant_id: response.data.tenant_id || '',
        document_library: response.data.document_library || 'Documents',
        enabled: response.data.enabled || false,
      });
    } catch (error) {
      console.error('Failed to fetch SharePoint settings:', error);
    } finally {
      setSharepointLoading(false);
    }
  };

  const fetchMetaSettings = async () => {
    setMetaLoading(true);
    try {
      const response = await api.get('/admin/meta-leads/settings');
      setMetaSettings({
        app_id: response.data.app_id || '',
        app_secret: response.data.app_secret || '',
        access_token: response.data.access_token || '',
        page_id: response.data.page_id || '',
        form_id: response.data.form_id || '',
        webhook_verify_token: response.data.webhook_verify_token || '',
        enabled: response.data.enabled || false,
      });
    } catch (error) {
      console.error('Failed to fetch Meta settings:', error);
    } finally {
      setMetaLoading(false);
    }
  };

  const saveMetaSettings = async () => {
    setMetaSaving(true);
    try {
      await api.post('/admin/meta-leads/settings', metaSettings);
      toast.success('Meta Leads settings saved');
    } catch (error) {
      toast.error('Failed to save Meta settings');
    } finally {
      setMetaSaving(false);
    }
  };

  const fetchMetaAccess = async () => {
    setMetaAccessLoading(true);
    try {
      const response = await api.get('/meta-leads/access');
      setMetaAccessUsers(response.data.all_users || []);
      setMetaAllowedIds(response.data.allowed_user_ids || []);
    } catch {
      toast.error('Failed to load access settings');
    } finally {
      setMetaAccessLoading(false);
    }
  };

  const saveMetaAccess = async () => {
    setMetaAccessSaving(true);
    try {
      await api.post('/meta-leads/access', { allowed_user_ids: metaAllowedIds });
      toast.success('Access settings saved');
    } catch {
      toast.error('Failed to save access settings');
    } finally {
      setMetaAccessSaving(false);
    }
  };

  const toggleMetaUserAccess = (userId) => {
    setMetaAllowedIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const fetchMonitoringSettings = async () => {
    setMonitoringLoading(true);
    try {
      const resp = await api.get('/admin/monitoring/settings');
      setMonitoringSettings(resp.data);
    } catch { /* not configured yet */ }
    finally { setMonitoringLoading(false); }
  };

  const saveMonitoringSettings = async () => {
    setMonitoringSaving(true);
    try {
      await api.post('/admin/monitoring/settings', monitoringSettings);
      toast.success('Monitoring settings saved');
      fetchMonitoringSettings();
    } catch (err) { toast.error('Failed to save monitoring settings'); }
    finally { setMonitoringSaving(false); }
  };

  const [savingPlatform, setSavingPlatform] = useState('');
  const saveMonitoringPlatform = async (platform) => {
    setSavingPlatform(platform);
    try {
      await api.post('/admin/monitoring/settings', monitoringSettings);
      toast.success(`${platform} settings saved`);
      fetchMonitoringSettings();
    } catch (err) { toast.error(`Failed to save ${platform} settings`); }
    finally { setSavingPlatform(''); }
  };

  useEffect(() => {
    fetchTemplates();
    if (user?.is_admin) {
      fetchXeroSettings();
      fetchSharepointSettings();
      fetchMetaSettings();
      fetchMetaAccess();
      fetchEmailConfig();
      fetchEmailBranding();
      fetchStatusNotifConfig();
      fetchAllStaff();
      fetchChecklistTemplate();
      fetchPhoneConfig();
      fetchEmailImages();
      fetchMonitoringSettings();
    }
  }, [user]);

  // Handle Xero OAuth callback params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const xeroResult = params.get('xero');
    if (xeroResult === 'success') {
      toast.success('Xero connected successfully!');
      fetchXeroSettings();
      window.history.replaceState({}, '', window.location.pathname);
    } else if (xeroResult === 'error') {
      const msg = params.get('msg') || 'Connection failed';
      toast.error(`Xero connection failed: ${msg}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const openEditDialog = async (trigger) => {
    try {
      const response = await emailTemplatesAPI.getByTrigger(trigger);
      const template = response.data;
      setFormData({
        subject: template.subject || '',
        body_html: template.body_html || '',
      });
      setTemplateEditorMode('html');
      setEditDialog({ open: true, template: { ...template, trigger } });
    } catch (error) {
      toast.error('Failed to load template');
    }
  };

  const handleSave = async () => {
    if (!editDialog.template) return;
    
    setSaving(true);
    try {
      await emailTemplatesAPI.create({
        trigger: editDialog.template.trigger,
        subject: formData.subject,
        // Convert text → HTML on save so the stored template is consistent HTML
        body_html: resolveBodyHtml(formData.body_html, templateEditorMode),
        active: true,
      });
      toast.success('Template saved successfully');
      setEditDialog({ open: false, template: null });
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  // Insert HTML tags at cursor position in textarea
  const insertHtml = (startTag, endTag) => {
    const textarea = document.getElementById('template-body-textarea');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.body_html.substring(start, end);
    const before = formData.body_html.substring(0, start);
    const after = formData.body_html.substring(end);
    
    const newContent = before + startTag + selectedText + endTag + after;
    setFormData({...formData, body_html: newContent});
    
    // Reset cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + startTag.length, start + startTag.length + selectedText.length);
    }, 0);
  };

  const handleResetToDefault = async (trigger) => {
    try {
      await emailTemplatesAPI.delete(trigger);
      toast.success('Template reset to default');
      fetchTemplates();
    } catch (error) {
      toast.info('Template is already using default');
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplateData.trigger || !newTemplateData.subject) {
      toast.error('Template name and subject are required');
      return;
    }
    setSaving(true);
    try {
      const trigger = newTemplateData.trigger.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
      await emailTemplatesAPI.create({
        trigger,
        subject: newTemplateData.subject,
        body_html: newTemplateData.body_html,
        active: true,
        is_manual: true,
        label: newTemplateData.trigger,
      });
      toast.success('Template created successfully');
      setCreateDialog(false);
      setNewTemplateData({ trigger: '', label: '', subject: '', body_html: '' });
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create template');
    } finally {
      setSaving(false);
    }
  };

  // Send email from template handlers
  const openSendDialog = async (template) => {
    setSendDialog({ open: true, template });
    setSendCustomerId('');
    setSendSubject(template.subject || '');
    setSendBody(template.body_html || '');
    setSendEditorMode('html');
    // Fetch customers list
    try {
      const res = await api.get('/customers');
      setCustomersList(res.data || []);
    } catch {
      setCustomersList([]);
    }
  };

  const replaceCustomerVars = (text, customer) => {
    if (!text || !customer) return text;
    const replacements = {
      '{{first_name}}': customer.first_name || '',
      '{{last_name}}': customer.last_name || '',
      '{{name}}': `${customer.first_name || ''} ${customer.last_name || ''}`.trim(),
      '{{email}}': customer.email || '',
      '{{phone}}': customer.phone || '',
      '{{address}}': customer.address || '',
      '{{city}}': customer.city || '',
      '{{postcode}}': customer.postcode || '',
      '{{system_size}}': String(customer.system_size || ''),
      '{{system_total_cost}}': String(customer.system_total_cost || ''),
      '{{deposit_amount}}': String(customer.deposit_amount || ''),
      '{{final_bill_amount}}': String(customer.final_bill_amount || ''),
      '{{provisional_install_date}}': customer.provisional_install_date || '',
      '{{confirmed_install_date}}': customer.confirmed_install_date || '',
      '{{installation_type}}': customer.installation_type || '',
      '{{installation_days}}': String(customer.installation_days || 1),
      '{{status}}': customer.status || '',
    };
    let result = text;
    Object.entries(replacements).forEach(([key, val]) => {
      result = result.replace(new RegExp(key.replace(/[{}]/g, '\\$&'), 'g'), val);
    });
    return result;
  };

  const handleCustomerSelectForSend = (customerId) => {
    setSendCustomerId(customerId);
    const customer = customersList.find(c => c.id === customerId);
    if (customer && sendDialog.template) {
      setSendSubject(replaceCustomerVars(sendDialog.template.subject || '', customer));
      setSendBody(replaceCustomerVars(sendDialog.template.body_html || '', customer));
    }
  };

  const handleSendTemplateEmail = async () => {
    if (!sendCustomerId) {
      toast.error('Please select a customer');
      return;
    }
    const customer = customersList.find(c => c.id === sendCustomerId);
    if (!customer) return;
    setSendingEmail(true);
    try {
      const formData = new FormData();
      formData.append('subject', sendSubject);
      formData.append('body_html', resolveBodyHtml(sendBody, sendEditorMode));
      formData.append('cc_emails', '');
      await api.post(`/customers/${sendCustomerId}/send-email`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Email sent to ${customer.first_name} ${customer.last_name}`);
      setSendDialog({ open: false, template: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Replace template variables with sample data
  const replaceVariables = (text) => {
    if (!text) return '';
    let result = text;
    Object.entries(SAMPLE_CUSTOMER).forEach(([key, value]) => {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
    });
    return result;
  };

  // Open preview dialog
  const openPreviewDialog = async (trigger) => {
    try {
      const response = await emailTemplatesAPI.getByTrigger(trigger);
      const template = response.data;
      setPreviewDialog({ 
        open: true, 
        template: { 
          ...template, 
          trigger,
          previewSubject: replaceVariables(template.subject),
          previewBody: replaceVariables(template.body_html),
        } 
      });
    } catch (error) {
      toast.error('Failed to load template for preview');
    }
  };

  const handleSaveSharepointSettings = async () => {
    setSharepointSaving(true);
    try {
      await api.post('/settings/sharepoint', sharepointSettings);
      toast.success('SharePoint settings saved successfully');
      fetchSharepointSettings();
    } catch (error) {
      toast.error('Failed to save SharePoint settings');
    } finally {
      setSharepointSaving(false);
    }
  };

  // Push every unsynced customer document + signed contract pack to SharePoint
  // immediately (same job as the nightly midnight sweep).
  const handleBackupAllToSharepoint = async () => {
    setBackupAllRunning(true);
    setBackupAllResult(null);
    const t = toast.loading('Backing up all documents to SharePoint…');
    try {
      const { data } = await api.post('/admin/documents/sync-all-sharepoint');
      const synced = (data.documents_synced || 0) + (data.packs_synced || 0);
      const failed = (data.documents_failed || 0) + (data.packs_failed || 0);
      setBackupAllResult(data);
      toast.success(`SharePoint backup complete — ${synced} backed up${failed ? `, ${failed} failed` : ''}`, { id: t, duration: 8000 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Backup failed', { id: t });
    } finally {
      setBackupAllRunning(false);
    }
  };

  // Remove duplicate customer document entries created by the SharePoint
  // two-way sync re-importing files. Runs a dry-run preview, then confirms.
  const handleDedupeDocuments = async () => {
    setDedupeRunning(true);
    setDedupeResult(null);
    const t = toast.loading('Scanning for duplicate documents…');
    try {
      const { data: preview } = await api.post('/admin/documents/dedupe?dry_run=true');
      const found = preview.total_duplicates_removed || 0;
      if (found === 0) {
        toast.success('No duplicate documents found — everything is clean.', { id: t, duration: 6000 });
        setDedupeResult({ ...preview, cleaned: true });
        return;
      }
      const ok = window.confirm(
        `Found ${found} duplicate document(s) across ${preview.customers_affected} customer(s).\n\n` +
        `Remove them now? The original copy of each file is kept.`
      );
      if (!ok) {
        toast.dismiss(t);
        setDedupeResult(preview);
        return;
      }
      const { data } = await api.post('/admin/documents/dedupe?dry_run=false');
      setDedupeResult({ ...data, cleaned: true });
      toast.success(`Removed ${data.total_duplicates_removed} duplicate document(s).`, { id: t, duration: 8000 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dedupe failed', { id: t });
    } finally {
      setDedupeRunning(false);
    }
  };

  // Scan all customer documents for files with no durable backup (at risk of
  // showing corrupt after a redeploy).
  const handleDocumentHealth = async () => {
    setHealthRunning(true);
    setHealthResult(null);
    const t = toast.loading('Scanning documents for backup health…');
    try {
      const { data } = await api.get('/admin/documents/health');
      setHealthResult(data);
      if (data.at_risk_count === 0) {
        toast.success(`All ${data.total_documents} documents have a durable backup.`, { id: t, duration: 6000 });
      } else {
        toast.warning(`${data.at_risk_count} document(s) have no durable backup.`, { id: t, duration: 6000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Health check failed', { id: t });
    } finally {
      setHealthRunning(false);
    }
  };

  const triggerHealthRepair = (customerId, documentId) => {
    setHealthRepairTarget({ customerId, documentId });
    if (healthRepairInputRef.current) {
      healthRepairInputRef.current.value = '';
      healthRepairInputRef.current.click();
    }
  };

  const handleHealthRepairFile = async (e) => {
    const file = e.target.files?.[0];
    const target = healthRepairTarget;
    if (!file || !target) return;
    setHealthRepairingId(target.documentId);
    const t = toast.loading('Re-uploading file…');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/customers/${target.customerId}/documents/${target.documentId}/replace`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('File re-uploaded and backed up', { id: t });
      await handleDocumentHealth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-upload failed', { id: t });
    } finally {
      setHealthRepairingId(null);
      setHealthRepairTarget(null);
    }
  };


  const fetchStatusNotifConfig = async () => {
    setStatusNotifLoading(true);
    try {
      const response = await api.get('/settings/status-notifications');
      setStatusNotifConfig(response.data.config || {});
    } catch (error) {
      console.error('Failed to fetch status notification config');
    } finally {
      setStatusNotifLoading(false);
    }
  };

  const fetchAllStaff = async () => {
    try {
      const response = await api.get('/admin/users');
      setAllStaff(response.data);
    } catch (error) {
      console.error('Failed to fetch staff list');
    }
  };

  const handleSaveStatusNotifConfig = async () => {
    setStatusNotifSaving(true);
    try {
      await api.post('/settings/status-notifications', { config: statusNotifConfig });
      toast.success('Process flow configuration saved');
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setStatusNotifSaving(false);
    }
  };

  const toggleStaffForStatus = (status, staffId) => {
    setStatusNotifConfig(prev => {
      const current = prev[status] || [];
      const updated = current.includes(staffId)
        ? current.filter(id => id !== staffId)
        : [...current, staffId];
      return { ...prev, [status]: updated };
    });
  };

  const fetchChecklistTemplate = async () => {
    setTemplateLoading(true);
    try {
      const response = await api.get('/settings/checklist-template');
      setTemplateItems(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch checklist template');
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleSaveChecklistTemplate = async () => {
    setTemplateSaving(true);
    try {
      await api.post('/settings/checklist-template', { items: templateItems });
      toast.success('Checklist template saved');
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setTemplateSaving(false);
    }
  };

  const addTemplateItem = () => {
    if (!newTemplateItem.trim()) return;
    setTemplateItems(prev => [...prev, newTemplateItem.trim()]);
    setNewTemplateItem('');
  };

  const removeTemplateItem = (index) => {
    setTemplateItems(prev => prev.filter((_, i) => i !== index));
  };

  const moveTemplateItem = (index, direction) => {
    const newItems = [...templateItems];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= newItems.length) return;
    [newItems[index], newItems[swapIdx]] = [newItems[swapIdx], newItems[index]];
    setTemplateItems(newItems);
  };

  const fetchPhoneConfig = async () => {
    setPhoneConfigLoading(true);
    try {
      const response = await api.get('/settings/phone-config');
      setPhoneConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch phone config');
    } finally {
      setPhoneConfigLoading(false);
    }
  };

  const handleSavePhoneConfig = async () => {
    setPhoneConfigSaving(true);
    try {
      await api.post('/settings/phone-config', {
        users: phoneConfig.map(u => ({
          user_id: u.user_id,
          extension: u.extension || '',
          direct_dial: u.direct_dial || '',
          mobile: u.mobile || '',
        })),
      });
      toast.success('Phone configuration saved');
    } catch (error) {
      toast.error('Failed to save phone config');
    } finally {
      setPhoneConfigSaving(false);
    }
  };

  const updatePhoneField = (userId, field, value) => {
    setPhoneConfig(prev => prev.map(u =>
      u.user_id === userId ? { ...u, [field]: value } : u
    ));
  };

  const fetchEmailImages = async () => {
    setEmailImagesLoading(true);
    try {
      const response = await api.get('/email-images');
      setEmailImages(response.data || []);
    } catch (error) {
      console.error('Failed to fetch email images');
    } finally {
      setEmailImagesLoading(false);
    }
  };

  const handleUploadImage = async () => {
    if (!newImageFile || !newImageSlug.trim()) {
      toast.error('Please select a file and enter a URL slug');
      return;
    }
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', newImageFile);
      formData.append('slug', newImageSlug.trim());
      await api.post('/email-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Image uploaded successfully');
      setNewImageSlug('');
      setNewImageFile(null);
      if (imageFileRef.current) imageFileRef.current.value = '';
      fetchEmailImages();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to upload image';
      toast.error(msg);
    } finally {
      setImageUploading(false);
    }
  };

  const handleDeleteImage = async (imageId, slug) => {
    if (!window.confirm(`Delete image "${slug}"? This will break any emails using this image URL.`)) return;
    try {
      await api.delete(`/email-images/${imageId}`);
      toast.success('Image deleted');
      fetchEmailImages();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('URL copied to clipboard');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const fetchEmailConfig = async () => {
    setEmailConfigLoading(true);
    try {
      const response = await api.get('/settings/email-config');
      setEmailConfig({
        smtp_host: response.data.smtp_host || 'smtp.office365.com',
        smtp_port: response.data.smtp_port || 587,
        smtp_username: response.data.smtp_username || '',
        smtp_password: '',
        from_email: response.data.from_email || '',
        use_tls: response.data.use_tls !== false,
        mention_emails_enabled: response.data.mention_emails_enabled !== false,
        daily_digest_enabled: response.data.daily_digest_enabled !== false,
        staff_kit_reminder_enabled: response.data.staff_kit_reminder_enabled === true,
        password_set: response.data.password_set || false,
        configured: response.data.configured || false,
      });
    } catch (error) {
      console.error('Failed to fetch email config');
    } finally {
      setEmailConfigLoading(false);
    }
  };

  const handleSaveEmailConfig = async () => {
    setEmailConfigSaving(true);
    try {
      await api.post('/settings/email-config', {
        smtp_host: emailConfig.smtp_host,
        smtp_port: emailConfig.smtp_port,
        smtp_username: emailConfig.smtp_username,
        smtp_password: emailConfig.smtp_password,
        from_email: emailConfig.from_email,
        use_tls: emailConfig.use_tls,
        mention_emails_enabled: emailConfig.mention_emails_enabled,
        daily_digest_enabled: emailConfig.daily_digest_enabled,
        staff_kit_reminder_enabled: emailConfig.staff_kit_reminder_enabled,
      });
      toast.success('Email configuration saved');
      fetchEmailConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email config');
    } finally {
      setEmailConfigSaving(false);
    }
  };

  const fetchEmailBranding = async () => {
    try {
      const { data } = await api.get('/settings/email-branding');
      setEmailBranding({
        company_name: data.company_name || '',
        registration_line: data.registration_line || '',
        registered_office: data.registered_office || '',
        phone: data.phone || '',
        website: data.website || '',
      });
    } catch (error) {
      console.error('Failed to fetch email branding');
    }
  };

  const handleSaveEmailBranding = async () => {
    setEmailBrandingSaving(true);
    try {
      await api.post('/settings/email-branding', emailBranding);
      toast.success('Email footer saved — applies to all automated emails');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save email footer');
    } finally {
      setEmailBrandingSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.current || !passwordData.new_password || !passwordData.confirm) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (passwordData.new_password !== passwordData.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    setChangingPassword(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.current,
        new_password: passwordData.new_password,
      });
      toast.success('Password changed successfully');
      setPasswordData({ current: '', new_password: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">Settings</h1>
          <p className="text-slate-500 mt-1">Manage your account, email templates, and integrations</p>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="email-templates">Email Templates</TabsTrigger>
            {user?.is_admin && <TabsTrigger value="email-config">Email Config</TabsTrigger>}
            {user?.is_admin && <TabsTrigger value="ms365">Microsoft 365</TabsTrigger>}
            {user?.is_admin && <TabsTrigger value="process-flow">Process Flow</TabsTrigger>}
            {user?.is_admin && <TabsTrigger value="checklist-template">Checklist Template</TabsTrigger>}
            {user?.is_admin && <TabsTrigger value="phone-config">3CX / Phone</TabsTrigger>}
            {user?.is_admin && <TabsTrigger value="yeastar" data-testid="tab-yeastar">Yeastar Phone</TabsTrigger>}
            {user?.is_admin && <TabsTrigger value="xero">Xero</TabsTrigger>}
            {user?.is_admin && <TabsTrigger value="sharepoint">SharePoint</TabsTrigger>}
            {user?.is_admin && <TabsTrigger value="meta-leads">Meta Leads</TabsTrigger>}
            {user?.is_admin && <TabsTrigger value="wix-leads">Wix / Website</TabsTrigger>}
            {user?.is_admin && <TabsTrigger value="email-images">Email Images</TabsTrigger>}
            {user?.is_admin && <TabsTrigger value="monitoring">Monitoring</TabsTrigger>}
            {user?.is_admin && <TabsTrigger value="messaging">Messaging</TabsTrigger>}
            {user?.is_admin && <TabsTrigger value="instagram">Instagram</TabsTrigger>}
            {user?.is_admin && <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">WhatsApp</TabsTrigger>}
            {user?.is_admin && <TabsTrigger value="banners" data-testid="tab-banners">Banners</TabsTrigger>}
            {user?.is_admin && <TabsTrigger value="website-chat" data-testid="tab-website-chat">Website Chat</TabsTrigger>}
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold font-['Manrope']">
                      Profile Information
                    </CardTitle>
                    <CardDescription>Your account details</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-2xl font-bold">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold text-slate-900">{user?.name}</h3>
                        <p className="text-slate-500">{user?.email}</p>
                      </div>
                    </div>
                    <Separator />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <User className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-sm text-slate-500">Full Name</p>
                          <p className="font-medium text-slate-900">{user?.name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-sm text-slate-500">Email</p>
                          <p className="font-medium text-slate-900">{user?.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-sm text-slate-500">Member Since</p>
                          <p className="font-medium text-slate-900">{formatDate(user?.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-sm text-slate-500">Role</p>
                          <Badge className="bg-blue-100 text-blue-700 border-blue-200">Admin</Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Change Password */}
                <Card className="border-slate-200 mt-6">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                      <Lock className="w-5 h-5 text-slate-600" />
                      Change Password
                    </CardTitle>
                    <CardDescription>Update your account password</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="current-password">Current Password</Label>
                      <Input
                        id="current-password"
                        type="password"
                        value={passwordData.current}
                        onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                        placeholder="Enter current password"
                        data-testid="current-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-password">New Password</Label>
                      <Input
                        id="new-password"
                        type="password"
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                        placeholder="At least 6 characters"
                        data-testid="new-password-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Confirm New Password</Label>
                      <Input
                        id="confirm-password"
                        type="password"
                        value={passwordData.confirm}
                        onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                        placeholder="Re-enter new password"
                        data-testid="confirm-password-input"
                      />
                    </div>
                    <Button
                      onClick={handleChangePassword}
                      disabled={changingPassword}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                      data-testid="change-password-btn"
                    >
                      {changingPassword ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <KeyRound className="w-4 h-4 mr-2" />
                      )}
                      Change Password
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Company Info Sidebar */}
              <div>
                <Card className="border-slate-200">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                        <Sun className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-base font-semibold font-['Manrope']">
                          Helios
                        </CardTitle>
                        <CardDescription>Electrical & Solar</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600">
                      system for managing customer projects from lead to completion.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Email Templates Tab */}
          <TabsContent value="email-templates">
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      Email Templates
                    </CardTitle>
                    <CardDescription>
                      Customize email templates for each status trigger or create your own. Use variables like {'{'}{'{'} first_name {'}'}{'}'}  to personalize.
                    </CardDescription>
                  </div>
                  <Button onClick={() => setCreateDialog(true)} data-testid="create-template-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Template
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Variables Reference */}
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm font-medium text-slate-700 mb-2">Available Variables:</p>
                    <div className="flex flex-wrap gap-2">
                      {TEMPLATE_VARIABLES.map((v) => (
                        <code key={v} className="text-xs bg-white px-2 py-1 rounded border border-slate-200">
                          {v}
                        </code>
                      ))}
                    </div>
                  </div>

                  {/* Templates List */}
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {EMAIL_TRIGGERS.map((trigger) => {
                        const template = templates.find(t => t.trigger === trigger.value);
                        const isCustom = template && !template.is_default;
                        
                        return (
                          <div
                            key={trigger.value}
                            className="flex items-center justify-between p-4 rounded-lg border border-slate-200 hover:border-blue-300 transition-colors"
                            data-testid={`template-${trigger.value}`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-slate-900">{trigger.label}</h4>
                                {isCustom ? (
                                  <Badge className="bg-blue-100 text-blue-700 text-xs">Custom</Badge>
                                ) : (
                                  <Badge className="bg-slate-100 text-slate-600 text-xs">Default</Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-500 mt-1">{trigger.description}</p>
                            </div>
                            <div className="flex gap-2">
                              {isCustom && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleResetToDefault(trigger.value)}
                                  data-testid={`reset-${trigger.value}`}
                                >
                                  <RotateCcw className="w-4 h-4 mr-1" />
                                  Reset
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openPreviewDialog(trigger.value)}
                                data-testid={`preview-${trigger.value}`}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Preview
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditDialog(trigger.value)}
                                data-testid={`edit-${trigger.value}`}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => {
                                  const tmpl = templates.find(t => t.trigger === trigger.value) || { trigger: trigger.value, subject: trigger.label, body_html: '' };
                                  openSendDialog(tmpl);
                                }}
                                data-testid={`send-${trigger.value}`}
                              >
                                <Send className="w-4 h-4 mr-1" />
                                Send
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Custom/Manual Templates */}
                  {templates.filter(t => t.is_manual).length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Custom Templates
                      </h4>
                      <div className="space-y-3">
                        {templates.filter(t => t.is_manual).map((template) => (
                          <div
                            key={template.trigger}
                            className="flex items-center justify-between p-4 rounded-lg border border-amber-200 bg-amber-50/50 hover:border-amber-300 transition-colors"
                            data-testid={`template-custom-${template.trigger}`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="font-medium text-slate-900">{template.label || template.trigger}</h4>
                                <Badge className="bg-amber-100 text-amber-700 text-xs">Manual</Badge>
                              </div>
                              <p className="text-sm text-slate-500 mt-1">Subject: {template.subject}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setFormData({ subject: template.subject, body_html: template.body_html || '' });
                                  setEditDialog({ open: true, template: { ...template, trigger: template.trigger } });
                                }}
                                data-testid={`edit-custom-${template.trigger}`}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={() => openSendDialog(template)}
                                data-testid={`send-custom-${template.trigger}`}
                              >
                                <Send className="w-4 h-4 mr-1" />
                                Send
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={async () => {
                                  try {
                                    await emailTemplatesAPI.delete(template.trigger);
                                    toast.success('Template deleted');
                                    fetchTemplates();
                                  } catch {
                                    toast.error('Failed to delete template');
                                  }
                                }}
                                data-testid={`delete-custom-${template.trigger}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Email Config Tab */}
          <TabsContent value="email-config">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope']">
                  Email Configuration
                </CardTitle>
                <CardDescription>
                  Configure SMTP settings for automated notifications and @mention emails
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {emailConfigLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <>
                    {/* Status indicator */}
                    <div className={`p-4 rounded-lg border ${emailConfig.configured ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                      <div className="flex items-center gap-3">
                        {emailConfig.configured ? (
                          <>
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                            <div>
                              <p className="font-medium text-emerald-800">Email Configured</p>
                              <p className="text-sm text-emerald-600">SMTP credentials are set. Automated emails are active.</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-5 h-5 text-amber-600" />
                            <div>
                              <p className="font-medium text-amber-800">Email Not Configured</p>
                              <p className="text-sm text-amber-600">Enter your SMTP credentials below to enable email notifications.</p>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* SMTP Settings */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>SMTP Server</Label>
                        <Input
                          value={emailConfig.smtp_host}
                          onChange={(e) => setEmailConfig({ ...emailConfig, smtp_host: e.target.value })}
                          placeholder="smtp.office365.com"
                          data-testid="email-smtp-host"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Port</Label>
                        <Input
                          type="number"
                          value={emailConfig.smtp_port}
                          onChange={(e) => setEmailConfig({ ...emailConfig, smtp_port: parseInt(e.target.value) || 587 })}
                          placeholder="587"
                          data-testid="email-smtp-port"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Username / Email</Label>
                        <Input
                          value={emailConfig.smtp_username}
                          onChange={(e) => setEmailConfig({ ...emailConfig, smtp_username: e.target.value })}
                          placeholder="your-email@company.com"
                          data-testid="email-smtp-username"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Password {emailConfig.password_set && <span className="text-xs text-emerald-600">(saved)</span>}</Label>
                        <Input
                          type="password"
                          value={emailConfig.smtp_password}
                          onChange={(e) => setEmailConfig({ ...emailConfig, smtp_password: e.target.value })}
                          placeholder={emailConfig.password_set ? 'Leave blank to keep current' : 'Enter password'}
                          data-testid="email-smtp-password"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>From Email</Label>
                        <Input
                          value={emailConfig.from_email}
                          onChange={(e) => setEmailConfig({ ...emailConfig, from_email: e.target.value })}
                          placeholder="noreply@company.com"
                          data-testid="email-from"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Use TLS</Label>
                        <div className="flex items-center gap-3 mt-1">
                          <Switch
                            checked={emailConfig.use_tls}
                            onCheckedChange={(checked) => setEmailConfig({ ...emailConfig, use_tls: checked })}
                            data-testid="email-tls-toggle"
                          />
                          <span className="text-sm text-slate-600">{emailConfig.use_tls ? 'Enabled' : 'Disabled'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Mention Emails Toggle */}
                    <div className="p-4 bg-slate-50 rounded-lg border">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <AtSign className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="font-medium text-slate-900">@Mention Email Notifications</p>
                            <p className="text-sm text-slate-500">Send email when a user is @mentioned in internal notes</p>
                          </div>
                        </div>
                        <Switch
                          checked={emailConfig.mention_emails_enabled}
                          onCheckedChange={(checked) => setEmailConfig({ ...emailConfig, mention_emails_enabled: checked })}
                          data-testid="mention-emails-toggle"
                        />
                      </div>
                    </div>

                    {/* Daily Digest Toggle */}
                    <div className="p-4 bg-slate-50 rounded-lg border">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Mail className="w-5 h-5 text-amber-600" />
                          <div>
                            <p className="font-medium text-slate-900">Daily Digest Emails</p>
                            <p className="text-sm text-slate-500">Send a morning summary of unread notifications and stale customers to each staff member</p>
                          </div>
                        </div>
                        <Switch
                          checked={emailConfig.daily_digest_enabled !== false}
                          onCheckedChange={(checked) => setEmailConfig({ ...emailConfig, daily_digest_enabled: checked })}
                          data-testid="daily-digest-toggle"
                        />
                      </div>
                    </div>

                    {/* Install-team kit list reminder */}
                    <div className="p-4 bg-slate-50 rounded-lg border">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Package className="w-5 h-5 text-amber-600" />
                          <div>
                            <p className="font-medium text-slate-900">Install Team Kit-List Reminder</p>
                            <p className="text-sm text-slate-500">5 days before a confirmed install, email the calendar recipients the kit list + customer photos</p>
                          </div>
                        </div>
                        <Switch
                          checked={emailConfig.staff_kit_reminder_enabled === true}
                          onCheckedChange={(checked) => setEmailConfig({ ...emailConfig, staff_kit_reminder_enabled: checked })}
                          data-testid="staff-kit-reminder-toggle"
                        />
                      </div>
                    </div>

                    <Button
                      onClick={handleSaveEmailConfig}
                      disabled={emailConfigSaving}
                      className="bg-amber-500 hover:bg-amber-600 text-white"
                      data-testid="save-email-config-btn"
                    >
                      {emailConfigSaving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Email Configuration
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Email Footer / Legal Branding */}
            <Card className="mt-6" data-testid="email-branding-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-sky-600" />
                  Email Footer (legal details)
                </CardTitle>
                <CardDescription>
                  Shown in the footer of every automated email, under your logo.
                  Update here anytime — no redeploy needed.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Company name</Label>
                  <Input
                    value={emailBranding.company_name}
                    onChange={(e) => setEmailBranding({ ...emailBranding, company_name: e.target.value })}
                    placeholder="Helios Electrical and Solar Ltd"
                    data-testid="branding-company-name"
                  />
                </div>
                <div>
                  <Label>Registration line (company no. / ICO / VAT)</Label>
                  <Input
                    value={emailBranding.registration_line}
                    onChange={(e) => setEmailBranding({ ...emailBranding, registration_line: e.target.value })}
                    placeholder="Registered in England and Wales, Company Number 16015327 • ICO Registered • VAT Number: 479878401"
                    data-testid="branding-registration-line"
                  />
                </div>
                <div>
                  <Label>Registered office address</Label>
                  <Input
                    value={emailBranding.registered_office}
                    onChange={(e) => setEmailBranding({ ...emailBranding, registered_office: e.target.value })}
                    placeholder="Registered Office: 51 Burners Lane South, Kiln Farm, Milton Keynes, MK11 3HA"
                    data-testid="branding-registered-office"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={emailBranding.phone}
                      onChange={(e) => setEmailBranding({ ...emailBranding, phone: e.target.value })}
                      placeholder="01908 348250"
                      data-testid="branding-phone"
                    />
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input
                      value={emailBranding.website}
                      onChange={(e) => setEmailBranding({ ...emailBranding, website: e.target.value })}
                      placeholder="www.helioses.co.uk"
                      data-testid="branding-website"
                    />
                  </div>
                </div>
                <Button
                  onClick={handleSaveEmailBranding}
                  disabled={emailBrandingSaving}
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                  data-testid="save-email-branding-btn"
                >
                  {emailBrandingSaving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Email Footer
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Microsoft 365 Tab */}
          <TabsContent value="ms365">
            <MS365Settings />
          </TabsContent>

          {/* Process Flow Tab */}
          <TabsContent value="process-flow">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                  <GitBranch className="w-5 h-5 text-blue-600" />
                  Status Change Notification Flow
                </CardTitle>
                <CardDescription>
                  Configure which staff members receive a notification and email when a customer moves to each stage
                </CardDescription>
              </CardHeader>
              <CardContent>
                {statusNotifLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {STAGES.map((stage, idx) => {
                      const selectedIds = statusNotifConfig[stage.key] || [];
                      return (
                        <div key={stage.key} className="border rounded-lg overflow-hidden" data-testid={`stage-config-${stage.key}`}>
                          <div className="flex items-center gap-3 p-4 bg-slate-50 border-b">
                            <div className="flex items-center gap-2 flex-1">
                              {idx > 0 && (
                                <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-xs text-slate-500 font-medium">
                                  {idx}
                                </div>
                              )}
                              <Badge className={`${stage.color} border`}>
                                {stage.label}
                              </Badge>
                              {selectedIds.length > 0 && (
                                <span className="text-xs text-slate-500">
                                  {selectedIds.length} staff notified
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="p-4">
                            <div className="flex flex-wrap gap-2">
                              {allStaff.map((staff) => {
                                const isSelected = selectedIds.includes(staff.id);
                                return (
                                  <button
                                    key={staff.id}
                                    onClick={() => toggleStaffForStatus(stage.key, staff.id)}
                                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                                      isSelected
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-600'
                                    }`}
                                    data-testid={`toggle-${stage.key}-${staff.id}`}
                                  >
                                    {staff.name}
                                    {isSelected && <Check className="w-3 h-3 ml-1 inline" />}
                                  </button>
                                );
                              })}
                              {allStaff.length === 0 && (
                                <p className="text-sm text-slate-400">No active staff members found</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    <Button
                      onClick={handleSaveStatusNotifConfig}
                      disabled={statusNotifSaving}
                      className="bg-amber-500 hover:bg-amber-600 text-white mt-4"
                      data-testid="save-process-flow-btn"
                    >
                      {statusNotifSaving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Process Flow
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Checklist Template Tab */}
          <TabsContent value="checklist-template">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                  <ClipboardCheck className="w-5 h-5 text-amber-600" />
                  Default Checklist Template
                </CardTitle>
                <CardDescription>
                  Define the default installation tasks that auto-populate for every new customer. Changes only affect new checklists.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {templateLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-1" data-testid="template-items-list">
                      {templateItems.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border group"
                          data-testid={`template-item-${idx}`}
                        >
                          <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                            {idx + 1}
                          </span>
                          <span className="flex-1 text-sm text-slate-800">{item}</span>
                          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => moveTemplateItem(idx, -1)}
                              disabled={idx === 0}
                              data-testid={`move-up-${idx}`}
                            >
                              <ChevronUp className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              onClick={() => moveTemplateItem(idx, 1)}
                              disabled={idx === templateItems.length - 1}
                              data-testid={`move-down-${idx}`}
                            >
                              <ChevronDown className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-slate-400 hover:text-red-500"
                              onClick={() => removeTemplateItem(idx)}
                              data-testid={`remove-template-${idx}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {templateItems.length === 0 && (
                      <div className="text-center py-6 text-slate-400">
                        <ClipboardCheck className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No template items. Add tasks below.</p>
                      </div>
                    )}

                    <div className="flex gap-2 pt-2 border-t">
                      <Input
                        value={newTemplateItem}
                        onChange={(e) => setNewTemplateItem(e.target.value)}
                        placeholder="Add a new default task..."
                        className="flex-1"
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTemplateItem())}
                        data-testid="new-template-item-input"
                      />
                      <Button
                        onClick={addTemplateItem}
                        disabled={!newTemplateItem.trim()}
                        variant="outline"
                        data-testid="add-template-item-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>

                    <Button
                      onClick={handleSaveChecklistTemplate}
                      disabled={templateSaving}
                      className="bg-amber-500 hover:bg-amber-600 text-white mt-2"
                      data-testid="save-template-btn"
                    >
                      {templateSaving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Template
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* 3CX Phone Config Tab */}
          <TabsContent value="phone-config">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                  <Phone className="w-5 h-5 text-blue-600" />
                  3CX Phone Configuration
                </CardTitle>
                <CardDescription>
                  Configure each staff member's phone details for 3CX integration (click-to-call and incoming call popups)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {phoneConfigLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                ) : phoneConfig.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Phone className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No staff members found. Add users first.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="hidden md:grid grid-cols-[1fr,1fr,1fr,1fr] gap-3 px-4 pb-2 border-b text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <span>Staff Member</span>
                      <span>Extension</span>
                      <span>Direct Dial</span>
                      <span>Mobile</span>
                    </div>
                    {phoneConfig.map((user) => (
                      <div
                        key={user.user_id}
                        className="grid grid-cols-1 md:grid-cols-[1fr,1fr,1fr,1fr] gap-3 p-4 bg-slate-50 rounded-lg border"
                        data-testid={`phone-user-${user.user_id}`}
                      >
                        <div className="flex flex-col justify-center">
                          <span className="font-medium text-slate-900 text-sm">{user.name}</span>
                          <span className="text-xs text-slate-500">{user.email}</span>
                        </div>
                        <Input
                          value={user.extension}
                          onChange={(e) => updatePhoneField(user.user_id, 'extension', e.target.value)}
                          placeholder="e.g. 101"
                          className="text-sm"
                          data-testid={`ext-${user.user_id}`}
                        />
                        <Input
                          value={user.direct_dial}
                          onChange={(e) => updatePhoneField(user.user_id, 'direct_dial', e.target.value)}
                          placeholder="e.g. 01234 567890"
                          className="text-sm"
                          data-testid={`direct-${user.user_id}`}
                        />
                        <Input
                          value={user.mobile}
                          onChange={(e) => updatePhoneField(user.user_id, 'mobile', e.target.value)}
                          placeholder="e.g. 07700 900000"
                          className="text-sm"
                          data-testid={`mobile-${user.user_id}`}
                        />
                      </div>
                    ))}

                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3 mt-4">
                      <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-blue-800 text-sm">3CX Integration Pending</p>
                        <p className="text-sm text-blue-700 mt-1">
                          Phone details saved here will be used once the 3CX API is connected. Contact support with your 3CX version and API credentials to enable click-to-call and call popups.
                        </p>
                      </div>
                    </div>

                    <Button
                      onClick={handleSavePhoneConfig}
                      disabled={phoneConfigSaving}
                      className="bg-amber-500 hover:bg-amber-600 text-white mt-2"
                      data-testid="save-phone-config-btn"
                    >
                      {phoneConfigSaving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Save Phone Configuration
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Xero Integration Tab */}
          {user?.is_admin && (
            <TabsContent value="yeastar">
              <YeastarSettings />
            </TabsContent>
          )}

          {user?.is_admin && (
            <TabsContent value="xero">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Xero Integration
                  </CardTitle>
                  <CardDescription>
                    Connect to Xero for invoice creation, contact syncing, and payment tracking
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Stripe clearing bank account */}
                  <Card className="border-slate-200" data-testid="xero-stripe-bank-card">
                    <CardContent className="pt-6 space-y-3">
                      <div>
                        <h4 className="font-medium text-slate-900 flex items-center gap-2">
                          <Receipt className="w-4 h-4 text-slate-500" />
                          Stripe clearing bank account
                        </h4>
                        <p className="text-xs text-slate-500 mt-1">
                          When a customer pays via Stripe, the payment is recorded against this Xero bank account. Use a dedicated Stripe clearing account so it reconciles cleanly.
                        </p>
                      </div>
                      {!xeroStatus.connected ? (
                        <p className="text-xs text-amber-600">Connect Xero to choose a bank account.</p>
                      ) : (
                        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                          <select
                            className="flex-1 h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                            value={xeroApiSettings.stripe_bank_account_code || ''}
                            onChange={(e) => setXeroApiSettings({ ...xeroApiSettings, stripe_bank_account_code: e.target.value })}
                            data-testid="xero-stripe-bank-select"
                          >
                            <option value="">Auto (first payments-enabled account)</option>
                            {xeroBankAccounts.map((a) => (
                              <option key={a.code} value={a.code} disabled={a.payments_enabled === false}>
                                {a.name} ({a.code}){a.payments_enabled === false ? ' — payments disabled' : ''}
                              </option>
                            ))}
                          </select>
                          <Button onClick={handleSaveXeroApi} disabled={xeroApiSaving} data-testid="xero-stripe-bank-save" className="bg-emerald-600 hover:bg-emerald-700">
                            {xeroApiSaving ? 'Saving…' : 'Save'}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Invoicing Mode */}
                  <Card className="border-slate-200" data-testid="invoicing-mode-card">
                    <CardContent className="pt-6 space-y-4">
                      <div>
                        <h4 className="font-medium text-slate-900 flex items-center gap-2">
                          <Receipt className="w-4 h-4 text-slate-500" />
                          Invoicing Mode
                        </h4>
                        <p className="text-xs text-slate-500 mt-1">
                          Applies to <strong>new customers only</strong> — existing customers keep their current setup.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => handleSaveInvoicingConfig({ invoicing_mode: 'two_invoice' })}
                          disabled={invoicingSaving}
                          data-testid="mode-two-invoice"
                          className={`text-left p-4 rounded-lg border-2 transition-colors ${
                            invoicingConfig.invoicing_mode === 'two_invoice'
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-slate-900">Two invoices</span>
                            {invoicingConfig.invoicing_mode === 'two_invoice' && (
                              <CheckCircle className="w-4 h-4 text-blue-600" />
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1">Separate deposit invoice + final invoice (current default).</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSaveInvoicingConfig({ invoicing_mode: 'single_invoice' })}
                          disabled={invoicingSaving}
                          data-testid="mode-single-invoice"
                          className={`text-left p-4 rounded-lg border-2 transition-colors ${
                            invoicingConfig.invoicing_mode === 'single_invoice'
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-slate-900">Single invoice</span>
                            {invoicingConfig.invoicing_mode === 'single_invoice' && (
                              <CheckCircle className="w-4 h-4 text-blue-600" />
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-1">One invoice for the full total; collect the deposit, then the balance on completion.</p>
                        </button>
                      </div>
                      <div className="flex items-end gap-3">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Deposit %</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            className="w-28"
                            value={invoicingConfig.deposit_percent}
                            onChange={(e) => setInvoicingConfig(c => ({ ...c, deposit_percent: e.target.value }))}
                            data-testid="deposit-percent-input"
                          />
                        </div>
                        <Button
                          variant="outline"
                          disabled={invoicingSaving}
                          onClick={() => handleSaveInvoicingConfig({})}
                          data-testid="save-deposit-percent"
                        >
                          {invoicingSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                          Save deposit %
                        </Button>
                        <p className="text-xs text-slate-400 pb-2">
                          Used in single-invoice mode (e.g. 25% of the total). Solar PV / battery jobs are 0%-rated for VAT automatically.
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* API Configuration */}
                  <Card className="border-slate-200">
                    <CardContent className="pt-6 space-y-4">
                      <h4 className="font-medium text-slate-900 flex items-center gap-2">
                        <Key className="w-4 h-4 text-slate-500" />
                        API Configuration
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Client ID</Label>
                          <Input
                            value={xeroApiSettings.client_id}
                            onChange={(e) => setXeroApiSettings(s => ({ ...s, client_id: e.target.value }))}
                            placeholder="Your Xero Client ID"
                            data-testid="xero-client-id"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">
                            Client Secret {xeroApiSettings.client_secret_set && <span className="text-xs text-emerald-600 ml-1">(set)</span>}
                          </Label>
                          <Input
                            type="password"
                            value={xeroClientSecret}
                            onChange={(e) => setXeroClientSecret(e.target.value)}
                            placeholder={xeroApiSettings.client_secret_set ? '••••••••  (leave blank to keep current)' : 'Your Xero Client Secret'}
                            data-testid="xero-client-secret"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Redirect URI (Callback URL)</Label>
                        <Input
                          value={xeroApiSettings.redirect_uri}
                          onChange={(e) => setXeroApiSettings(s => ({ ...s, redirect_uri: e.target.value }))}
                          placeholder="https://app.helioses.co.uk/api/xero/callback"
                          data-testid="xero-redirect-uri"
                        />
                        <p className="text-xs text-slate-500">This must exactly match the redirect URI registered in your Xero Developer App</p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Scopes</Label>
                        <Input
                          value={xeroApiSettings.scopes}
                          onChange={(e) => setXeroApiSettings(s => ({ ...s, scopes: e.target.value }))}
                          placeholder="offline_access openid accounting.transactions..."
                          className="text-xs"
                          data-testid="xero-scopes"
                        />
                      </div>
                      <div className="flex justify-between items-center">
                        <Button
                          onClick={handleXeroDiagnose}
                          disabled={xeroDiagLoading}
                          variant="outline"
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          data-testid="xero-diagnose-btn"
                        >
                          {xeroDiagLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />}
                          Diagnose
                        </Button>
                        <Button
                          onClick={handleSaveXeroApi}
                          disabled={xeroApiSaving}
                          className="bg-slate-800 hover:bg-slate-900 text-white"
                          data-testid="xero-save-api-btn"
                        >
                          {xeroApiSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                          Save API Settings
                        </Button>
                      </div>
                      {xeroDiagnostics && (
                        <div className="mt-3 p-4 bg-slate-50 border border-slate-200 rounded-lg text-xs space-y-2" data-testid="xero-diagnostics-panel">
                          <div className="flex items-center justify-between">
                            <div className="font-semibold text-slate-700">Live OAuth Diagnostics</div>
                            <button
                              onClick={() => setXeroDiagnostics(null)}
                              className="text-slate-400 hover:text-slate-600"
                              data-testid="xero-diagnostics-close"
                            >×</button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div>
                              <div className="text-slate-500">Active Client ID</div>
                              <div className="font-mono break-all text-slate-900" data-testid="diag-client-id">
                                {xeroDiagnostics.active_client_id || <span className="text-rose-600">(empty!)</span>}
                              </div>
                              <div className="text-[10px] text-slate-400">
                                {xeroDiagnostics.active_client_id_length} chars · masked: {xeroDiagnostics.active_client_id_masked}
                              </div>
                            </div>
                            <div>
                              <div className="text-slate-500">Client Secret</div>
                              <div className={`font-medium ${xeroDiagnostics.active_client_secret_set ? 'text-emerald-700' : 'text-rose-600'}`}>
                                {xeroDiagnostics.active_client_secret_set ? '✓ Set' : '✗ Not set'}
                              </div>
                            </div>
                            <div className="md:col-span-2">
                              <div className="text-slate-500">Active Redirect URI (must match Xero Developer Portal exactly)</div>
                              <div className="font-mono break-all text-slate-900" data-testid="diag-redirect-uri">
                                {xeroDiagnostics.active_redirect_uri}
                              </div>
                            </div>
                            <div className="md:col-span-2">
                              <div className="text-slate-500">Active Scopes</div>
                              <div className="font-mono break-all text-slate-900 text-[11px]">{xeroDiagnostics.active_scopes}</div>
                            </div>
                          </div>
                          <div className="pt-2 border-t border-slate-200 text-[11px] text-slate-600">
                            <div className="font-medium mb-1">Sources</div>
                            <ul className="list-disc list-inside space-y-0.5">
                              <li>Client ID in DB: {xeroDiagnostics.sources.client_id_in_db ? '✓' : '✗'} · in env: {xeroDiagnostics.sources.client_id_in_env ? '✓' : '✗'}</li>
                              <li>Client Secret in DB: {xeroDiagnostics.sources.client_secret_in_db ? '✓' : '✗'} · in env: {xeroDiagnostics.sources.client_secret_in_env ? '✓' : '✗'}</li>
                              <li>Redirect URI in DB: {xeroDiagnostics.sources.redirect_uri_in_db ? '✓' : '✗'} · in env: {xeroDiagnostics.sources.redirect_uri_in_env ? '✓' : '✗'}</li>
                            </ul>
                          </div>
                          <div className="pt-2 border-t border-slate-200 text-[11px] text-amber-800 bg-amber-50 -mx-4 -mb-4 px-4 py-3 rounded-b-lg">
                            <div className="font-medium mb-1">💡 If Xero shows "unauthorized_client / Unknown client":</div>
                            <div className="text-slate-700">{xeroDiagnostics.help}</div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {xeroLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                  ) : xeroStatus.connected ? (
                    <>
                      {/* Connected State */}
                      <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                            <div>
                              <p className="font-medium text-emerald-800">Connected to Xero</p>
                              <p className="text-sm text-emerald-600">
                                Organisation: <strong>{xeroStatus.tenant_name || 'Unknown'}</strong>
                                {xeroStatus.connected_at && (
                                  <span className="ml-2 text-xs">
                                    &middot; Connected {formatDate(xeroStatus.connected_at)}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={handleXeroDisconnect}
                            data-testid="xero-disconnect-btn"
                          >
                            Disconnect
                          </Button>
                        </div>
                      </div>

                      {/* Sync Contacts */}
                      <Card className="border-slate-200">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-slate-900">Sync Contacts</h4>
                              <p className="text-sm text-slate-500">Push all active customers to Xero as contacts</p>
                            </div>
                            <Button
                              onClick={handleXeroSyncContacts}
                              disabled={xeroSyncing}
                              className="bg-blue-600 hover:bg-blue-700"
                              data-testid="xero-sync-contacts-btn"
                            >
                              {xeroSyncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                              Sync Now
                            </Button>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Sync Invoice Statuses */}
                      <Card className="border-slate-200">
                        <CardContent className="pt-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-slate-900">Sync Invoice Statuses</h4>
                              <p className="text-sm text-slate-500">Pull latest payment statuses from Xero for all open invoices</p>
                            </div>
                            <Button
                              onClick={handleXeroSyncInvoices}
                              disabled={xeroInvoiceSyncing}
                              className="bg-blue-600 hover:bg-blue-700"
                              data-testid="xero-sync-invoices-btn"
                            >
                              {xeroInvoiceSyncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                              Sync Invoices
                            </Button>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Xero Document Templates */}
                      <Card className="border-slate-200">
                        <CardContent className="pt-6 space-y-4">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-slate-900">Quote & Invoice Templates</h4>
                            <Button size="sm" onClick={() => { setEditingTemplate(null); setTemplateForm({ name: '', doc_type: 'invoice', reference_template: 'Helios - {{name}}', due_days: 30, is_default: false, line_items: [{ description: '', quantity: 1, unit_amount: 0, account_code: '200' }] }); setShowTemplateForm(true); }} data-testid="add-xero-template-btn">
                              <Plus className="w-4 h-4 mr-1" /> New Template
                            </Button>
                          </div>
                          <p className="text-sm text-slate-500">Pre-fill line items and references when creating quotes or invoices. Use variables like <code className="bg-slate-100 px-1 rounded text-xs">{'{{name}}'}</code>, <code className="bg-slate-100 px-1 rounded text-xs">{'{{address}}'}</code>, <code className="bg-slate-100 px-1 rounded text-xs">{'{{system_size}}'}</code>, <code className="bg-slate-100 px-1 rounded text-xs">{'{{deposit_amount}}'}</code>, <code className="bg-slate-100 px-1 rounded text-xs">{'{{system_total_cost}}'}</code></p>

                          {showTemplateForm && (
                            <Card className="border-blue-200 bg-blue-50/30">
                              <CardContent className="pt-4 space-y-3">
                                <div className="grid grid-cols-3 gap-3">
                                  <div className="space-y-1">
                                    <Label className="text-xs">Template Name</Label>
                                    <Input value={templateForm.name} onChange={(e) => setTemplateForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Standard Quote" data-testid="template-name" />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Type</Label>
                                    <Select value={templateForm.doc_type} onValueChange={(v) => setTemplateForm(f => ({ ...f, doc_type: v }))}>
                                      <SelectTrigger data-testid="template-doc-type"><SelectValue /></SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="invoice">Invoice</SelectItem>
                                        <SelectItem value="quote">Quote</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-xs">Due/Expiry Days</Label>
                                    <Input type="number" value={templateForm.due_days} onChange={(e) => setTemplateForm(f => ({ ...f, due_days: parseInt(e.target.value) || 30 }))} data-testid="template-due-days" />
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Reference Template</Label>
                                  <Input value={templateForm.reference_template} onChange={(e) => setTemplateForm(f => ({ ...f, reference_template: e.target.value }))} placeholder="Helios - {{name}}" data-testid="template-reference" />
                                </div>
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <Label className="text-xs">Line Items</Label>
                                    <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setTemplateForm(f => ({ ...f, line_items: [...f.line_items, { description: '', quantity: 1, unit_amount: 0, account_code: '200' }] }))}>+ Add Line</Button>
                                  </div>
                                  {templateForm.line_items.map((li, idx) => (
                                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                                      <Input className="col-span-6 text-sm" value={li.description} onChange={(e) => { const items = [...templateForm.line_items]; items[idx].description = e.target.value; setTemplateForm(f => ({ ...f, line_items: items })); }} placeholder="Description (use {{variables}})" data-testid={`template-line-desc-${idx}`} />
                                      <Input className="col-span-2 text-sm" type="number" value={li.quantity} onChange={(e) => { const items = [...templateForm.line_items]; items[idx].quantity = parseFloat(e.target.value) || 1; setTemplateForm(f => ({ ...f, line_items: items })); }} placeholder="Qty" />
                                      <Input className="col-span-3 text-sm" type="number" value={li.unit_amount} onChange={(e) => { const items = [...templateForm.line_items]; items[idx].unit_amount = parseFloat(e.target.value) || 0; setTemplateForm(f => ({ ...f, line_items: items })); }} placeholder="Unit £" step="0.01" />
                                      {templateForm.line_items.length > 1 && (
                                        <button className="col-span-1 text-red-400 hover:text-red-600" onClick={() => setTemplateForm(f => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }))}>
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                <div className="flex items-center gap-3">
                                  <label className="flex items-center gap-2 text-sm">
                                    <input type="checkbox" checked={templateForm.is_default} onChange={(e) => setTemplateForm(f => ({ ...f, is_default: e.target.checked }))} />
                                    Set as default for {templateForm.doc_type}s
                                  </label>
                                </div>
                                <div className="flex justify-end gap-2">
                                  <Button variant="outline" size="sm" onClick={() => setShowTemplateForm(false)}>Cancel</Button>
                                  <Button size="sm" onClick={handleSaveTemplate} data-testid="save-xero-template-btn">
                                    {editingTemplate ? 'Update' : 'Save'} Template
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          )}

                          {xeroDocTemplates.length > 0 && (
                            <div className="space-y-2">
                              {xeroDocTemplates.map(tmpl => (
                                <div key={tmpl.id} className="flex items-center justify-between p-3 border rounded-lg bg-white hover:bg-slate-50">
                                  <div className="flex items-center gap-3">
                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${tmpl.doc_type === 'quote' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                                      {tmpl.doc_type === 'quote' ? 'Quote' : 'Invoice'}
                                    </span>
                                    <div>
                                      <p className="text-sm font-medium text-slate-900">{tmpl.name} {tmpl.is_default && <span className="text-xs text-emerald-600">(Default)</span>}</p>
                                      <p className="text-xs text-slate-500">{tmpl.line_items?.length || 0} line items &middot; {tmpl.due_days} days</p>
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleEditTemplate(tmpl)}>Edit</Button>
                                    <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 hover:text-red-700" onClick={() => handleDeleteTemplate(tmpl.id)}>Delete</Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* How it works */}
                      <Card className="border-slate-200 bg-slate-50">
                        <CardContent className="pt-4">
                          <h4 className="font-medium text-slate-900 mb-2">What's connected:</h4>
                          <ul className="text-sm text-slate-600 space-y-1.5">
                            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> <strong>Contact Sync</strong> — Push customers to Xero contacts (Sync Now or auto on invoice)</li>
                            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> <strong>Auto Invoicing</strong> — Deposit invoice on Quote Accepted, Final invoice on Installation Complete</li>
                            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> <strong>Stripe Payments</strong> — Customers can pay online via the Customer Portal</li>
                            <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> <strong>Payment Sync</strong> — Xero and Stripe payments auto-update invoice status to Paid</li>
                          </ul>
                        </CardContent>
                      </Card>
                    </>
                  ) : (
                    <>
                      {/* Not Connected State */}
                      <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                        <div className="flex items-center gap-3">
                          <AlertCircle className="w-5 h-5 text-amber-600" />
                          <div>
                            <p className="font-medium text-amber-800">Not Connected</p>
                            <p className="text-sm text-amber-600">Click below to connect your Xero account via OAuth</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col items-center py-8 space-y-4">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                          <FileText className="w-8 h-8 text-blue-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900">Connect to Xero</h3>
                        <p className="text-sm text-slate-500 text-center max-w-md">
                          Link your Xero account to automatically sync contacts, create invoices, and track payments.
                        </p>
                        <Button
                          onClick={handleXeroConnect}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-6"
                          data-testid="xero-connect-btn"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Connect to Xero
                        </Button>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* SharePoint Integration Tab */}
          {user?.is_admin && (
            <TabsContent value="sharepoint">
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    SharePoint Integration
                  </CardTitle>
                  <CardDescription>
                    Connect to SharePoint to automatically upload customer documents
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {sharepointLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                  ) : (
                    <>
                      {/* Enable Toggle */}
                      <div className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium text-slate-900">Enable SharePoint Integration</p>
                          <p className="text-sm text-slate-500">Automatically upload documents to SharePoint when added to customers</p>
                        </div>
                        <Switch
                          checked={sharepointSettings.enabled}
                          onCheckedChange={(checked) => setSharepointSettings({...sharepointSettings, enabled: checked})}
                        />
                      </div>

                      {/* Credentials */}
                      <div className="space-y-4">
                        <h3 className="font-medium text-slate-900">SharePoint Configuration</h3>
                        <p className="text-sm text-slate-500">
                          Configure your Microsoft 365 SharePoint app registration credentials.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2 md:col-span-2">
                            <Label>SharePoint Site URL</Label>
                            <Input
                              value={sharepointSettings.site_url}
                              onChange={(e) => setSharepointSettings({...sharepointSettings, site_url: e.target.value})}
                              placeholder="https://yourcompany.sharepoint.com/sites/yoursite"
                              data-testid="sharepoint-site-url"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Client ID (Application ID)</Label>
                            <Input
                              value={sharepointSettings.client_id}
                              onChange={(e) => setSharepointSettings({...sharepointSettings, client_id: e.target.value})}
                              placeholder="Your Azure App Client ID"
                              data-testid="sharepoint-client-id"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Client Secret</Label>
                            <Input
                              type="password"
                              value={sharepointSettings.client_secret}
                              onChange={(e) => setSharepointSettings({...sharepointSettings, client_secret: e.target.value})}
                              placeholder={sharepointSettings.client_secret_set ? '••••••••••••••••' : 'Your Client Secret'}
                              data-testid="sharepoint-client-secret"
                            />
                            {sharepointSettings.client_secret_set && (
                              <p className="text-xs text-slate-500">Leave blank to keep existing secret</p>
                            )}
                          </div>
                          <div className="space-y-2">
                            <Label>Tenant ID</Label>
                            <Input
                              value={sharepointSettings.tenant_id}
                              onChange={(e) => setSharepointSettings({...sharepointSettings, tenant_id: e.target.value})}
                              placeholder="Your Azure Tenant ID"
                              data-testid="sharepoint-tenant-id"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Document Library</Label>
                            <Input
                              value={sharepointSettings.document_library}
                              onChange={(e) => setSharepointSettings({...sharepointSettings, document_library: e.target.value})}
                              placeholder="Documents"
                              data-testid="sharepoint-library"
                            />
                            <p className="text-xs text-slate-500">The name of the SharePoint document library</p>
                          </div>
                        </div>
                      </div>

                      {/* Save Button */}
                      <div className="flex justify-end pt-4 border-t">
                        <Button onClick={handleSaveSharepointSettings} disabled={sharepointSaving}>
                          {sharepointSaving ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Save SharePoint Settings
                        </Button>
                      </div>

                      {/* Back up all documents now */}
                      <Card className="border-sky-200 bg-sky-50">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="flex-1 min-w-[240px]">
                              <h4 className="font-medium text-slate-900 mb-1 flex items-center gap-2">
                                <Cloud className="w-4 h-4 text-sky-600" />
                                Back up all customer documents now
                              </h4>
                              <p className="text-sm text-slate-600">
                                Immediately push every customer document and signed contract pack
                                that isn't yet on SharePoint to its customer folder. Runs the same
                                job as the automatic nightly (midnight) sync — useful for pushing
                                older documents without waiting.
                              </p>
                            </div>
                            <Button
                              onClick={handleBackupAllToSharepoint}
                              disabled={backupAllRunning}
                              data-testid="backup-all-sharepoint-btn"
                            >
                              {backupAllRunning ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <RefreshCw className="w-4 h-4 mr-2" />
                              )}
                              {backupAllRunning ? 'Backing up…' : 'Back up all now'}
                            </Button>
                          </div>
                          {backupAllResult && (
                            <div className="mt-3 rounded-md bg-white border border-sky-200 px-3 py-2 text-sm text-slate-700" data-testid="backup-all-result">
                              <CheckCircle className="w-4 h-4 text-emerald-600 inline mr-1 -mt-0.5" />
                              <strong>{(backupAllResult.documents_synced || 0) + (backupAllResult.packs_synced || 0)}</strong> backed up
                              {((backupAllResult.documents_failed || 0) + (backupAllResult.packs_failed || 0)) > 0 && (
                                <span className="text-rose-600"> · {(backupAllResult.documents_failed || 0) + (backupAllResult.packs_failed || 0)} failed</span>
                              )}
                              {backupAllResult.documents_missing > 0 && (
                                <span className="text-amber-600"> · {backupAllResult.documents_missing} missing locally</span>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Remove duplicate documents */}
                      <Card className="border-amber-200 bg-amber-50">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="flex-1 min-w-[240px]">
                              <h4 className="font-medium text-slate-900 mb-1 flex items-center gap-2">
                                <Trash2 className="w-4 h-4 text-amber-600" />
                                Remove duplicate documents
                              </h4>
                              <p className="text-sm text-slate-600">
                                Scan every customer for duplicate document entries (where the same
                                SharePoint file was imported more than once) and remove the extras.
                                The original copy of each file is always kept. You'll see a preview
                                count before anything is deleted.
                              </p>
                            </div>
                            <Button
                              variant="outline"
                              onClick={handleDedupeDocuments}
                              disabled={dedupeRunning}
                              data-testid="dedupe-documents-btn"
                            >
                              {dedupeRunning ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4 mr-2" />
                              )}
                              {dedupeRunning ? 'Scanning…' : 'Find & remove duplicates'}
                            </Button>
                          </div>
                          {dedupeResult && (
                            <div className="mt-3 rounded-md bg-white border border-amber-200 px-3 py-2 text-sm text-slate-700" data-testid="dedupe-result">
                              <CheckCircle className="w-4 h-4 text-emerald-600 inline mr-1 -mt-0.5" />
                              {dedupeResult.total_duplicates_removed > 0 ? (
                                <>
                                  <strong>{dedupeResult.total_duplicates_removed}</strong> duplicate{dedupeResult.total_duplicates_removed === 1 ? '' : 's'}{' '}
                                  {dedupeResult.cleaned ? 'removed' : 'found (not yet removed)'} across{' '}
                                  <strong>{dedupeResult.customers_affected}</strong> customer(s)
                                </>
                              ) : (
                                <>No duplicate documents found.</>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Document health check */}
                      <Card className="border-sky-200 bg-sky-50">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="flex-1 min-w-[240px]">
                              <h4 className="font-medium text-slate-900 mb-1 flex items-center gap-2">
                                <ShieldCheck className="w-4 h-4 text-sky-600" />
                                Document health check
                              </h4>
                              <p className="text-sm text-slate-600">
                                Scan every customer's files for any that have <strong>no durable
                                backup</strong> (not stored in SharePoint or GridFS). These are the
                                files at risk of showing as "corrupt" after a redeploy — re-upload
                                them to make them safe.
                              </p>
                            </div>
                            <Button
                              variant="outline"
                              onClick={handleDocumentHealth}
                              disabled={healthRunning}
                              data-testid="document-health-btn"
                            >
                              {healthRunning ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <ShieldCheck className="w-4 h-4 mr-2" />
                              )}
                              {healthRunning ? 'Scanning…' : 'Run health check'}
                            </Button>
                          </div>
                          {healthResult && (
                            <div className="mt-3 space-y-2" data-testid="document-health-result">
                              <input
                                ref={healthRepairInputRef}
                                type="file"
                                className="hidden"
                                onChange={handleHealthRepairFile}
                                data-testid="health-repair-input"
                              />
                              <div className="rounded-md bg-white border border-sky-200 px-3 py-2 text-sm text-slate-700 flex flex-wrap gap-x-4 gap-y-1">
                                <span><strong>{healthResult.total_documents}</strong> total</span>
                                <span className="text-emerald-700"><strong>{healthResult.durable_backups}</strong> backed up</span>
                                <span className="text-slate-500"><strong>{healthResult.self_healing}</strong> self-healing</span>
                                <span className={healthResult.at_risk_count > 0 ? 'text-red-700' : 'text-slate-500'}>
                                  <strong>{healthResult.at_risk_count}</strong> at risk
                                </span>
                              </div>
                              {healthResult.at_risk_count > 0 && (
                                <div className="rounded-md bg-white border border-red-200 max-h-64 overflow-auto">
                                  <table className="w-full text-xs">
                                    <thead className="bg-red-50 text-red-800 sticky top-0">
                                      <tr>
                                        <th className="text-left px-3 py-1.5 font-medium">Customer</th>
                                        <th className="text-left px-3 py-1.5 font-medium">File</th>
                                        <th className="text-left px-3 py-1.5 font-medium">Uploaded</th>
                                        <th className="text-right px-3 py-1.5 font-medium">Fix</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {healthResult.at_risk.map((d) => (
                                        <tr key={d.document_id} className="border-t border-slate-100" data-testid="at-risk-row">
                                          <td className="px-3 py-1.5">
                                            <a href={`/customers/${d.customer_id}`} className="text-sky-600 hover:underline" target="_blank" rel="noreferrer">
                                              {d.customer_name}
                                            </a>
                                          </td>
                                          <td className="px-3 py-1.5 text-slate-700">{d.filename}</td>
                                          <td className="px-3 py-1.5 text-slate-400">{d.uploaded_at ? new Date(d.uploaded_at).toLocaleDateString('en-GB') : '—'}</td>
                                          <td className="px-3 py-1.5 text-right">
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="h-7 text-xs border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                                              onClick={() => triggerHealthRepair(d.customer_id, d.document_id)}
                                              disabled={healthRepairingId === d.document_id}
                                              data-testid={`health-reupload-${d.document_id}`}
                                            >
                                              {healthRepairingId === d.document_id ? (
                                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                              ) : (
                                                <ShieldCheck className="w-3 h-3 mr-1" />
                                              )}
                                              Re-upload
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Usage Instructions */}
                      <Card className="border-slate-200 bg-slate-50">
                        <CardContent className="pt-4">
                          <h4 className="font-medium text-slate-900 mb-2">Setup Instructions:</h4>
                          <ol className="list-decimal list-inside text-sm text-slate-600 space-y-1">
                            <li>Register an app in Azure Active Directory</li>
                            <li>Grant SharePoint Sites.ReadWrite.All API permission</li>
                            <li>Copy your Client ID, Client Secret, and Tenant ID here</li>
                            <li>Enter your SharePoint site URL</li>
                            <li>Enable the integration and save</li>
                            <li>Documents will be uploaded to a folder named after the customer's address</li>
                          </ol>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Meta Leads Tab */}
          {user?.is_admin && (
            <TabsContent value="meta-leads">
              <Card className="border-slate-200">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                      <Facebook className="w-5 h-5 text-blue-600" />
                      Meta Lead Ads Integration
                    </CardTitle>
                    <CardDescription>Connect to Facebook Lead Ads to automatically import leads</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="meta-enabled" className="text-sm">Enable Integration</Label>
                    <Switch
                      id="meta-enabled"
                      checked={metaSettings.enabled}
                      onCheckedChange={(checked) => setMetaSettings({...metaSettings, enabled: checked})}
                      data-testid="meta-enabled-switch"
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  {metaLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                  ) : (
                    <>
                      <div className="space-y-6">
                        {/* App Credentials */}
                        <div className="space-y-4">
                          <h4 className="font-medium text-slate-900">Facebook App Credentials</h4>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>App ID</Label>
                              <Input
                                value={metaSettings.app_id}
                                onChange={(e) => setMetaSettings({...metaSettings, app_id: e.target.value})}
                                placeholder="Your Facebook App ID"
                                data-testid="meta-app-id"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>App Secret</Label>
                              <Input
                                type="password"
                                value={metaSettings.app_secret}
                                onChange={(e) => setMetaSettings({...metaSettings, app_secret: e.target.value})}
                                placeholder="••••••••"
                                data-testid="meta-app-secret"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Access Token */}
                        <div className="space-y-2">
                          <Label>Page Access Token</Label>
                          <Input
                            type="password"
                            value={metaSettings.access_token}
                            onChange={(e) => setMetaSettings({...metaSettings, access_token: e.target.value})}
                            placeholder="Your Page Access Token (never expires token recommended)"
                            data-testid="meta-access-token"
                          />
                          <p className="text-xs text-slate-500">
                            Generate a Page Access Token from Facebook Developer Console with leads_retrieval permission
                          </p>
                        </div>

                        {/* Page and Form IDs */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Facebook Page ID</Label>
                            <Input
                              value={metaSettings.page_id}
                              onChange={(e) => setMetaSettings({...metaSettings, page_id: e.target.value})}
                              placeholder="Your Facebook Page ID"
                              data-testid="meta-page-id"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Lead Form ID</Label>
                            <Input
                              value={metaSettings.form_id}
                              onChange={(e) => setMetaSettings({...metaSettings, form_id: e.target.value})}
                              placeholder="Lead Form ID to fetch leads from"
                              data-testid="meta-form-id"
                            />
                          </div>
                        </div>

                        {/* Webhook Settings */}
                        <div className="space-y-2">
                          <Label>Webhook Verify Token</Label>
                          <Input
                            value={metaSettings.webhook_verify_token}
                            onChange={(e) => setMetaSettings({...metaSettings, webhook_verify_token: e.target.value})}
                            placeholder="A custom string for webhook verification"
                            data-testid="meta-webhook-token"
                          />
                          <p className="text-xs text-slate-500">
                            Use this token when setting up webhooks in Facebook Developer Console
                          </p>
                        </div>
                      </div>

                      {/* Save Button */}
                      <div className="flex justify-end pt-4 border-t mt-6">
                        <Button onClick={saveMetaSettings} disabled={metaSaving}>
                          {metaSaving ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Save Meta Leads Settings
                        </Button>
                      </div>

                      {/* Setup Instructions */}
                      <Card className="border-slate-200 bg-blue-50 mt-6">
                        <CardContent className="pt-4">
                          <h4 className="font-medium text-slate-900 mb-2">Setup Instructions:</h4>
                          <ol className="list-decimal list-inside text-sm text-slate-600 space-y-1">
                            <li>Create a Facebook App at <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">developers.facebook.com</a></li>
                            <li>Add the "Facebook Login" and "Lead Ads" products to your app</li>
                            <li>Request the <code className="bg-slate-100 px-1 rounded">leads_retrieval</code> and <code className="bg-slate-100 px-1 rounded">pages_read_engagement</code> permissions</li>
                            <li>Generate a Page Access Token with the required permissions</li>
                            <li>Find your Page ID and Lead Form ID from Facebook Business Manager</li>
                            <li>Enable the integration and save settings</li>
                            <li>Use the "Sync Leads" button on the Meta Leads page to import leads</li>
                          </ol>
                        </CardContent>
                      </Card>

                      {/* Access Control */}
                      <Card className="border-slate-200 mt-6">
                        <CardHeader>
                          <CardTitle className="text-base font-semibold font-['Manrope'] flex items-center gap-2">
                            <Shield className="w-4 h-4 text-blue-600" />
                            Access Control
                          </CardTitle>
                          <CardDescription>
                            Select which team members can view and manage Meta Leads. Admins always have access.
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {metaAccessLoading ? (
                            <div className="flex items-center justify-center py-6">
                              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {metaAccessUsers.map((u) => (
                                <div
                                  key={u.id}
                                  className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors"
                                  data-testid={`meta-access-user-${u.id}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                      <User className="w-4 h-4 text-blue-600" />
                                    </div>
                                    <div>
                                      <p className="font-medium text-sm text-slate-900">{u.name}</p>
                                      <p className="text-xs text-slate-500">{u.email}</p>
                                    </div>
                                    {u.is_admin && (
                                      <Badge className="bg-amber-100 text-amber-700 text-xs">Admin</Badge>
                                    )}
                                  </div>
                                  {u.is_admin ? (
                                    <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Always has access
                                    </Badge>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <Checkbox
                                        id={`meta-access-${u.id}`}
                                        checked={metaAllowedIds.includes(u.id)}
                                        onCheckedChange={() => toggleMetaUserAccess(u.id)}
                                        data-testid={`meta-access-checkbox-${u.id}`}
                                      />
                                      <Label htmlFor={`meta-access-${u.id}`} className="text-sm cursor-pointer">
                                        Grant access
                                      </Label>
                                    </div>
                                  )}
                                </div>
                              ))}
                              {metaAccessUsers.length === 0 && (
                                <p className="text-center text-slate-400 py-4 text-sm">No users found</p>
                              )}
                              <div className="flex justify-end pt-3 border-t">
                                <Button size="sm" onClick={saveMetaAccess} disabled={metaAccessSaving} data-testid="save-meta-access-btn">
                                  {metaAccessSaving ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  ) : (
                                    <Save className="w-4 h-4 mr-2" />
                                  )}
                                  Save Access
                                </Button>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Email Images Tab */}
          {user?.is_admin && (
            <TabsContent value="wix-leads">
              <WixLeadsSettings />
            </TabsContent>
          )}

          {/* Email Images Tab */}
          {user?.is_admin && (
            <TabsContent value="email-images">
              <Card className="border-slate-200">
                <CardHeader>
                  <div className="space-y-1">
                    <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                      <Image className="w-5 h-5 text-purple-600" />
                      Email Image Hosting
                    </CardTitle>
                    <CardDescription>Upload images and get permanent URLs for use in HTML email templates</CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Upload form */}
                  <Card className="border-dashed border-2 border-slate-300 bg-slate-50">
                    <CardContent className="pt-6">
                      <div className="flex items-end gap-4">
                        <div className="flex-1 space-y-2">
                          <Label className="text-sm font-medium">Image File</Label>
                          <Input
                            ref={imageFileRef}
                            type="file"
                            accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                            onChange={(e) => setNewImageFile(e.target.files?.[0] || null)}
                            data-testid="email-image-file-input"
                          />
                        </div>
                        <div className="flex-1 space-y-2">
                          <Label className="text-sm font-medium">URL Slug</Label>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400 whitespace-nowrap">.../api/email-images/</span>
                            <Input
                              value={newImageSlug}
                              onChange={(e) => setNewImageSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                              placeholder="e.g. company-logo"
                              data-testid="email-image-slug-input"
                            />
                          </div>
                        </div>
                        <Button
                          onClick={handleUploadImage}
                          disabled={imageUploading || !newImageFile || !newImageSlug.trim()}
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          data-testid="upload-email-image-btn"
                        >
                          {imageUploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                          Upload
                        </Button>
                      </div>
                      {newImageSlug && (
                        <p className="text-xs text-slate-500 mt-2">
                          URL will be: <code className="bg-slate-200 px-1 py-0.5 rounded">{BASE_URL}/api/email-images/{newImageSlug.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')}</code>
                        </p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Image library */}
                  {emailImagesLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                  ) : emailImages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                      <Image className="w-12 h-12 mb-4" />
                      <p>No images uploaded yet</p>
                      <p className="text-sm">Upload your first image to use in HTML emails</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="email-images-grid">
                      {emailImages.map((img) => {
                        const imageUrl = `${BASE_URL}/api/email-images/${img.slug}`;
                        return (
                          <Card key={img.id} className="border-slate-200 overflow-hidden group" data-testid={`email-image-${img.slug}`}>
                            <div className="aspect-video bg-slate-100 flex items-center justify-center overflow-hidden border-b">
                              <img
                                src={imageUrl}
                                alt={img.slug}
                                className="max-w-full max-h-full object-contain"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </div>
                            <CardContent className="p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-sm text-slate-800 truncate">{img.slug}</span>
                                <Badge className="text-xs bg-slate-100 text-slate-600 border-slate-200">{formatFileSize(img.size)}</Badge>
                              </div>
                              <div className="flex items-center gap-1 bg-slate-50 rounded px-2 py-1.5 border">
                                <Link className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                <code className="text-xs text-slate-600 truncate flex-1">{imageUrl}</code>
                                <button
                                  onClick={() => copyToClipboard(imageUrl)}
                                  className="ml-1 p-0.5 hover:bg-slate-200 rounded transition-colors flex-shrink-0"
                                  title="Copy URL"
                                  data-testid={`copy-url-${img.slug}`}
                                >
                                  <Copy className="w-3.5 h-3.5 text-slate-500" />
                                </button>
                              </div>
                              <div className="flex items-center justify-between text-xs text-slate-400">
                                <span>{img.uploaded_by} &middot; {formatDate(img.created_at)}</span>
                                <button
                                  onClick={() => handleDeleteImage(img.id, img.slug)}
                                  className="p-1 hover:bg-red-50 hover:text-red-500 rounded transition-colors"
                                  data-testid={`delete-image-${img.slug}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                              <div className="text-xs text-slate-400">
                                HTML: <code className="bg-slate-100 px-1 py-0.5 rounded text-slate-500 select-all cursor-pointer" onClick={() => copyToClipboard(`<img src="${imageUrl}" alt="${img.slug}" />`)}>{`<img src="..." alt="${img.slug}" />`}</code>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Monitoring Tab */}
          {user?.is_admin && (
            <TabsContent value="monitoring">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-green-500" />
                    Monitoring Platform Credentials
                  </CardTitle>
                  <CardDescription>
                    Configure API credentials for Fox ESS Cloud and Growatt monitoring platforms
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {monitoringLoading ? (
                    <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
                  ) : (
                    <>
                      {/* Fox ESS */}
                      <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                          <Zap className="w-4 h-4 text-amber-500" />
                          Fox ESS Cloud
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="foxess-api-key">API Key</Label>
                            <Input
                              id="foxess-api-key"
                              type="password"
                              placeholder="Enter Fox ESS API Key"
                              value={monitoringSettings.foxess_api_key || ''}
                              onChange={(e) => setMonitoringSettings(s => ({ ...s, foxess_api_key: e.target.value }))}
                              data-testid="foxess-api-key"
                            />
                          </div>
                          <div>
                            <Label htmlFor="foxess-client-id">Client ID</Label>
                            <Input
                              id="foxess-client-id"
                              placeholder="Enter Fox ESS Client ID"
                              value={monitoringSettings.foxess_client_id || ''}
                              onChange={(e) => setMonitoringSettings(s => ({ ...s, foxess_client_id: e.target.value }))}
                              data-testid="foxess-client-id"
                            />
                          </div>
                          <div>
                            <Label htmlFor="foxess-client-secret">Client Secret</Label>
                            <Input
                              id="foxess-client-secret"
                              type="password"
                              placeholder="Enter Fox ESS Client Secret"
                              value={monitoringSettings.foxess_client_secret || ''}
                              onChange={(e) => setMonitoringSettings(s => ({ ...s, foxess_client_secret: e.target.value }))}
                              data-testid="foxess-client-secret"
                            />
                          </div>
                          <div>
                            <Label htmlFor="foxess-redirect-uri">OAuth Redirect URI</Label>
                            <Input
                              id="foxess-redirect-uri"
                              placeholder="https://app.helioses.co.uk/api/monitoring/foxess/callback"
                              value={monitoringSettings.foxess_redirect_uri || ''}
                              onChange={(e) => setMonitoringSettings(s => ({ ...s, foxess_redirect_uri: e.target.value }))}
                              data-testid="foxess-redirect-uri"
                            />
                          </div>
                        </div>
                        <Button size="sm" onClick={() => saveMonitoringPlatform('Fox ESS')} disabled={savingPlatform === 'Fox ESS'} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-foxess-btn">
                          {savingPlatform === 'Fox ESS' ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Saving...</> : 'Save Fox ESS'}
                        </Button>
                      </div>

                      {/* Growatt */}
                      <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                          <Zap className="w-4 h-4 text-green-500" />
                          Growatt
                        </h3>
                        <div>
                          <Label htmlFor="growatt-token">API Token</Label>
                          <Input
                            id="growatt-token"
                            type="password"
                            placeholder="Enter Growatt API Token"
                            value={monitoringSettings.growatt_api_token || ''}
                            onChange={(e) => setMonitoringSettings(s => ({ ...s, growatt_api_token: e.target.value }))}
                            data-testid="growatt-api-token"
                          />
                        </div>
                        <Button size="sm" onClick={() => saveMonitoringPlatform('Growatt')} disabled={savingPlatform === 'Growatt'} className="bg-green-600 hover:bg-green-700 text-white" data-testid="save-growatt-btn">
                          {savingPlatform === 'Growatt' ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Saving...</> : 'Save Growatt'}
                        </Button>
                      </div>

                      {/* Solis Cloud */}
                      <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                          <Zap className="w-4 h-4 text-orange-500" />
                          Solis Cloud
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="solis-key-id">Key ID</Label>
                            <Input
                              id="solis-key-id"
                              placeholder="1300386381677860895"
                              value={monitoringSettings.solis_key_id || ''}
                              onChange={(e) => setMonitoringSettings(s => ({ ...s, solis_key_id: e.target.value }))}
                              data-testid="solis-key-id"
                            />
                          </div>
                          <div>
                            <Label htmlFor="solis-key-secret">Key Secret</Label>
                            <Input
                              id="solis-key-secret"
                              type="password"
                              placeholder="Enter Solis Key Secret"
                              value={monitoringSettings.solis_key_secret || ''}
                              onChange={(e) => setMonitoringSettings(s => ({ ...s, solis_key_secret: e.target.value }))}
                              data-testid="solis-key-secret"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <Label htmlFor="solis-api-url">API URL</Label>
                            <Input
                              id="solis-api-url"
                              placeholder="https://www.soliscloud.com:13333"
                              value={monitoringSettings.solis_api_url || ''}
                              onChange={(e) => setMonitoringSettings(s => ({ ...s, solis_api_url: e.target.value }))}
                              data-testid="solis-api-url"
                            />
                          </div>
                        </div>
                        <Button size="sm" onClick={() => saveMonitoringPlatform('Solis')} disabled={savingPlatform === 'Solis'} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="save-solis-btn">
                          {savingPlatform === 'Solis' ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Saving...</> : 'Save Solis'}
                        </Button>
                      </div>

                      {/* Tesla */}
                      <div className="space-y-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                          <Zap className="w-4 h-4 text-red-500" />
                          Tesla Energy (Fleet API)
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="tesla-client-id">Client ID</Label>
                            <Input
                              id="tesla-client-id"
                              placeholder="281966a9-1557-..."
                              value={monitoringSettings.tesla_client_id || ''}
                              onChange={(e) => setMonitoringSettings(s => ({ ...s, tesla_client_id: e.target.value }))}
                              data-testid="tesla-client-id"
                            />
                          </div>
                          <div>
                            <Label htmlFor="tesla-client-secret">Client Secret</Label>
                            <Input
                              id="tesla-client-secret"
                              type="password"
                              placeholder="ta-secret...."
                              value={monitoringSettings.tesla_client_secret || ''}
                              onChange={(e) => setMonitoringSettings(s => ({ ...s, tesla_client_secret: e.target.value }))}
                              data-testid="tesla-client-secret"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <Label htmlFor="tesla-redirect-uri">OAuth Redirect URI</Label>
                            <Input
                              id="tesla-redirect-uri"
                              placeholder="https://app.helioses.co.uk/api/monitoring/tesla/callback"
                              value={monitoringSettings.tesla_redirect_uri || ''}
                              onChange={(e) => setMonitoringSettings(s => ({ ...s, tesla_redirect_uri: e.target.value }))}
                              data-testid="tesla-redirect-uri"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => {
                              const cid = monitoringSettings.tesla_client_id;
                              const ruri = monitoringSettings.tesla_redirect_uri || 'https://app.helioses.co.uk/api/monitoring/tesla/callback';
                              if (!cid) { toast.error('Enter Tesla Client ID first'); return; }
                              const url = `https://auth.tesla.com/oauth2/v3/authorize?response_type=code&client_id=${cid}&redirect_uri=${encodeURIComponent(ruri)}&scope=energy_device_data+energy_cmds+offline_access&state=helios`;
                              window.open(url, '_blank');
                            }}
                            data-testid="tesla-connect-btn"
                          >
                            Connect Tesla Account
                          </Button>
                          <span className="text-xs text-slate-500">Opens Tesla login to authorise access to energy products</span>
                        </div>
                        <Button size="sm" onClick={() => saveMonitoringPlatform('Tesla')} disabled={savingPlatform === 'Tesla'} className="bg-red-600 hover:bg-red-700 text-white" data-testid="save-tesla-btn">
                          {savingPlatform === 'Tesla' ? <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Saving...</> : 'Save Tesla'}
                        </Button>
                      </div>

                      <Button
                        onClick={saveMonitoringSettings}
                        disabled={monitoringSaving}
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                        data-testid="save-monitoring-btn"
                      >
                        {monitoringSaving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</> : 'Save Monitoring Settings'}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Messaging Tab */}
          {user?.is_admin && (
            <TabsContent value="messaging">
              <Card>
                <CardHeader>
                  <CardTitle>Facebook & Instagram Messaging</CardTitle>
                  <CardDescription>Configure your Meta Page Access Token for Messenger and Instagram DMs</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Page ID</Label>
                      <Input
                        value={msgSettings.page_id || ''}
                        onChange={(e) => setMsgSettings(s => ({ ...s, page_id: e.target.value }))}
                        placeholder="Your Facebook Page ID"
                        data-testid="msg-page-id"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Instagram Account ID <span className="text-xs text-slate-400">(optional)</span></Label>
                      <Input
                        value={msgSettings.instagram_account_id || ''}
                        onChange={(e) => setMsgSettings(s => ({ ...s, instagram_account_id: e.target.value }))}
                        placeholder="Leave blank to use Page ID"
                        data-testid="msg-ig-id"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>
                      Page Access Token {msgSettings.page_access_token === '***' && <span className="text-xs text-emerald-600 ml-1">(set)</span>}
                    </Label>
                    <Input
                      type="password"
                      value={msgTokenInput}
                      onChange={(e) => setMsgTokenInput(e.target.value)}
                      placeholder={msgSettings.page_access_token === '***' ? '••••••••  (leave blank to keep current)' : 'Paste your permanent Page Access Token'}
                      data-testid="msg-access-token"
                    />
                    <p className="text-xs text-slate-500">Use a permanent Page Access Token from Meta Developer Console. Short-lived tokens expire after 1 hour.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Webhook Verify Token</Label>
                    <Input
                      value={msgSettings.webhook_verify_token || ''}
                      onChange={(e) => setMsgSettings(s => ({ ...s, webhook_verify_token: e.target.value }))}
                      placeholder="Custom string for webhook verification"
                      data-testid="msg-webhook-token"
                    />
                    <p className="text-xs text-slate-500">Must match the verify token in your Meta Developer App webhook settings. Webhook URL: <code className="bg-slate-100 px-1 rounded">https://app.helioses.co.uk/api/messaging/webhook</code></p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Button onClick={handleSaveMsgSettings} disabled={msgSaving} data-testid="msg-save-btn">
                      {msgSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Settings
                    </Button>
                    <Button variant="outline" onClick={handleSyncMessages} disabled={msgSyncing} data-testid="msg-sync-btn">
                      {msgSyncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Sync Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Instagram Tab */}
          {user?.is_admin && (
            <TabsContent value="instagram">
              <Card>
                <CardHeader>
                  <CardTitle>Instagram API</CardTitle>
                  <CardDescription>Configure your Instagram API app for DM messaging</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Instagram App ID</Label>
                      <Input
                        value={igSettings.ig_app_id || ''}
                        onChange={(e) => setIgSettings(s => ({ ...s, ig_app_id: e.target.value }))}
                        placeholder="e.g. 908245248848412"
                        data-testid="ig-app-id"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Instagram Account ID <span className="text-xs text-slate-400">(from Step 2)</span></Label>
                      <Input
                        value={igSettings.ig_account_id || ''}
                        onChange={(e) => setIgSettings(s => ({ ...s, ig_account_id: e.target.value }))}
                        placeholder="Your Instagram Business Account ID"
                        data-testid="ig-account-id"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>
                        App Secret {igSettings.ig_app_secret_set && <span className="text-xs text-emerald-600 ml-1">(set)</span>}
                      </Label>
                      <Input
                        type="password"
                        value={igAppSecret}
                        onChange={(e) => setIgAppSecret(e.target.value)}
                        placeholder={igSettings.ig_app_secret_set ? '••••••••  (leave blank to keep)' : 'Instagram App Secret'}
                        data-testid="ig-app-secret"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>
                        Access Token {igSettings.ig_access_token_set && <span className="text-xs text-emerald-600 ml-1">(set)</span>}
                      </Label>
                      <Input
                        type="password"
                        value={igAccessToken}
                        onChange={(e) => setIgAccessToken(e.target.value)}
                        placeholder={igSettings.ig_access_token_set ? '••••••••  (leave blank to keep)' : 'From Step 2: Add account'}
                        data-testid="ig-access-token"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Webhook Verify Token</Label>
                    <Input
                      value={igSettings.ig_webhook_verify_token || ''}
                      onChange={(e) => setIgSettings(s => ({ ...s, ig_webhook_verify_token: e.target.value }))}
                      placeholder="Custom string for webhook verification"
                      data-testid="ig-webhook-token"
                    />
                  </div>
                  <Card className="border-slate-200 bg-slate-50">
                    <CardContent className="pt-4">
                      <h4 className="font-medium text-slate-900 mb-2">Webhook Setup (Step 3 in Meta)</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Callback URL</p>
                          <code className="text-sm bg-white px-3 py-1.5 rounded border block">https://app.helioses.co.uk/api/instagram/webhook</code>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 mb-1">Verify Token</p>
                          <code className="text-sm bg-white px-3 py-1.5 rounded border block">{igSettings.ig_webhook_verify_token || 'helios_meta_webhook_2026'}</code>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <div className="flex items-center gap-3">
                    <Button onClick={handleSaveIgSettings} disabled={igSaving} data-testid="ig-save-btn">
                      {igSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Settings
                    </Button>
                    <Button variant="outline" onClick={handleSyncInstagram} disabled={igSyncing} data-testid="ig-sync-btn">
                      {igSyncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Sync Instagram DMs
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {user?.is_admin && (
            <TabsContent value="banners">
              <BannersSettings />
            </TabsContent>
          )}

          {user?.is_admin && (
            <TabsContent value="whatsapp">
              <WhatsAppConnect />
            </TabsContent>
          )}

          {user?.is_admin && (
            <TabsContent value="website-chat">
              <Card data-testid="website-chat-settings">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessagesSquare className="w-5 h-5 text-blue-600" />
                    Website Chat
                  </CardTitle>
                  <CardDescription>
                    Set your office hours and the one-tap quick replies staff can use in the chat.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-8">
                  {/* Office hours */}
                  <div>
                    <h4 className="font-medium text-slate-900 mb-1">Office hours</h4>
                    <p className="text-xs text-slate-500 mb-3">
                      Outside these hours, visitors get the "agents are busy" holding reply immediately (instead of after 5 minutes). Times are UK (Europe/London).
                    </p>
                    <div className="space-y-2" data-testid="office-hours-editor">
                      {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map((day) => {
                        const d = chatConfig.office_hours?.[day] || { enabled: false, open: '09:00', close: '17:00' };
                        const setDay = (patch) => setChatConfig(c => ({
                          ...c,
                          office_hours: { ...c.office_hours, [day]: { ...d, ...patch } },
                        }));
                        return (
                          <div key={day} className="flex items-center gap-3 flex-wrap">
                            <label className="flex items-center gap-2 w-40">
                              <input
                                type="checkbox"
                                checked={!!d.enabled}
                                onChange={(e) => setDay({ enabled: e.target.checked })}
                                className="w-4 h-4"
                                data-testid={`oh-${day}-enabled`}
                              />
                              <span className="capitalize text-sm text-slate-700">{day}</span>
                            </label>
                            <Input
                              type="time"
                              value={d.open || '09:00'}
                              disabled={!d.enabled}
                              onChange={(e) => setDay({ open: e.target.value })}
                              className="w-32"
                              data-testid={`oh-${day}-open`}
                            />
                            <span className="text-slate-400 text-sm">to</span>
                            <Input
                              type="time"
                              value={d.close || '17:00'}
                              disabled={!d.enabled}
                              onChange={(e) => setDay({ close: e.target.value })}
                              className="w-32"
                              data-testid={`oh-${day}-close`}
                            />
                            {!d.enabled && <span className="text-xs text-slate-400">Closed</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Canned replies */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-slate-900">Quick replies</h4>
                        <p className="text-xs text-slate-500">One-tap replies staff can drop into a chat.</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setChatConfig(c => ({ ...c, canned_replies: [...(c.canned_replies || []), { label: '', text: '' }] }))}
                        data-testid="add-canned-reply"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add
                      </Button>
                    </div>
                    <div className="space-y-3" data-testid="canned-replies-editor">
                      {(chatConfig.canned_replies || []).map((c, i) => (
                        <div key={i} className="flex items-start gap-2 p-3 border border-slate-200 rounded-lg">
                          <div className="flex-1 space-y-2">
                            <Input
                              placeholder="Button label (e.g. Opening hours)"
                              value={c.label || ''}
                              onChange={(e) => setChatConfig(cf => {
                                const list = [...cf.canned_replies]; list[i] = { ...list[i], label: e.target.value }; return { ...cf, canned_replies: list };
                              })}
                              data-testid={`canned-label-${i}`}
                            />
                            <Textarea
                              placeholder="Reply text sent to the visitor"
                              rows={2}
                              value={c.text || ''}
                              onChange={(e) => setChatConfig(cf => {
                                const list = [...cf.canned_replies]; list[i] = { ...list[i], text: e.target.value }; return { ...cf, canned_replies: list };
                              })}
                              data-testid={`canned-text-${i}`}
                            />
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setChatConfig(cf => ({ ...cf, canned_replies: cf.canned_replies.filter((_, idx) => idx !== i) }))}
                            data-testid={`remove-canned-${i}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                      {(chatConfig.canned_replies || []).length === 0 && (
                        <p className="text-sm text-slate-400 py-3 text-center">No quick replies yet — add one above.</p>
                      )}
                    </div>
                  </div>

                  <Button onClick={handleSaveChatConfig} disabled={chatConfigSaving} data-testid="save-chat-config">
                    {chatConfigSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save website chat settings
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          )}

        </Tabs>

        {/* Edit Template Dialog */}
        <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, template: null })}>
          <DialogContent className="max-w-[92vw] w-[92vw] h-[88vh] flex flex-col p-0 gap-0" data-testid="edit-template-dialog">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex-shrink-0">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-lg">
                  <Code className="w-5 h-5 text-amber-500" />
                  Edit Email Template
                </DialogTitle>
                <DialogDescription>
                  Customize the email for: <span className="font-medium text-slate-700">{STATUS_DISPLAY_NAMES[editDialog.template?.trigger] || editDialog.template?.trigger}</span>
                </DialogDescription>
              </DialogHeader>
            </div>

            {/* Subject Line */}
            <div className="px-6 py-3 border-b border-slate-100 flex-shrink-0">
              <div className="flex items-center gap-3">
                <Label className="text-sm font-medium text-slate-600 whitespace-nowrap">Subject:</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData({...formData, subject: e.target.value})}
                  placeholder="e.g., Helios Electrical and Solar - {{name}}"
                  className="flex-1"
                  data-testid="template-subject"
                />
              </div>
            </div>

            {/* Split View: Editor + Preview */}
            <div className="flex-1 grid grid-cols-2 min-h-0 overflow-hidden" data-testid="split-view-container">
              {/* Left Pane - HTML Code Editor */}
              <div className="flex flex-col border-r border-slate-200 min-h-0">
                <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200 flex-shrink-0">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                    <Code className="w-3.5 h-3.5" />
                    {templateEditorMode === 'html' ? 'HTML Code' : 'Plain Text'}
                  </span>
                  <div className="flex items-center gap-2">
                    <EmailModeToggle
                      mode={templateEditorMode}
                      onChange={setTemplateEditorMode}
                      body={formData.body_html}
                      setBody={(v) => setFormData((prev) => ({...prev, body_html: v}))}
                      testIdPrefix="template-editor-mode"
                    />
                  {templateEditorMode === 'html' && (
                  <div className="flex flex-wrap gap-0.5">
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => insertHtml('<b>', '</b>')} title="Bold" data-testid="toolbar-bold">
                      <span className="font-bold">B</span>
                    </Button>
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => insertHtml('<i>', '</i>')} title="Italic" data-testid="toolbar-italic">
                      <span className="italic">I</span>
                    </Button>
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => insertHtml('<u>', '</u>')} title="Underline" data-testid="toolbar-underline">
                      <span className="underline">U</span>
                    </Button>
                    <div className="w-px bg-slate-300 mx-0.5" />
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => insertHtml('<h2 style="color: #1e3a5f; margin: 16px 0 8px;">', '</h2>')} title="Heading">
                      H2
                    </Button>
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => insertHtml('<p style="margin: 8px 0;">', '</p>')} title="Paragraph">
                      P
                    </Button>
                    <div className="w-px bg-slate-300 mx-0.5" />
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => insertHtml('<a href="URL" style="color: #2563eb;">', '</a>')} title="Link">
                      Link
                    </Button>
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => insertHtml('<br>', '')} title="Line Break">
                      BR
                    </Button>
                    <div className="w-px bg-slate-300 mx-0.5" />
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => insertHtml('<div style="background: #f8fafc; padding: 16px; border-radius: 8px; margin: 16px 0;">', '</div>')} title="Info Box">
                      Box
                    </Button>
                    <Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => insertHtml('<button style="background: #2563eb; color: white; padding: 12px 24px; border-radius: 6px; border: none; font-weight: 600;">', '</button>')} title="Button">
                      Btn
                    </Button>
                  </div>
                  )}
                  </div>
                </div>
                <div className="flex-1 min-h-0 relative">
                  <textarea
                    id="template-body-textarea"
                    value={formData.body_html}
                    onChange={(e) => setFormData({...formData, body_html: e.target.value})}
                    placeholder={templateEditorMode === 'html'
                      ? "Paste your HTML here (e.g. from Canva, Mailchimp, or write your own)&#10;&#10;<html>&#10;<body>&#10;  <h1>Hello {{first_name}}</h1>&#10;  <p>Your email content here...</p>&#10;</body>&#10;</html>"
                      : "Write plain text — line breaks become paragraphs in the email.\n\nHi {{first_name}},\n\nYour content here..."}
                    className={`absolute inset-0 w-full h-full resize-none text-sm p-4 border-0 focus:outline-none focus:ring-0 ${templateEditorMode === 'html' ? 'font-mono bg-slate-900 text-emerald-300' : 'bg-white text-slate-800 leading-relaxed'}`}
                    style={{ tabSize: 2 }}
                    spellCheck={templateEditorMode === 'text'}
                    data-testid="template-body"
                  />
                </div>
                <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 flex-shrink-0">
                  <div className="flex flex-wrap gap-1.5">
                    {TEMPLATE_VARIABLES.map((v) => (
                      <button
                        key={v}
                        onClick={() => {
                          const ta = document.getElementById('template-body-textarea');
                          if (!ta) return;
                          const start = ta.selectionStart;
                          const before = formData.body_html.substring(0, start);
                          const after = formData.body_html.substring(ta.selectionEnd);
                          setFormData({...formData, body_html: before + v + after});
                          setTimeout(() => { ta.focus(); ta.setSelectionRange(start + v.length, start + v.length); }, 0);
                        }}
                        className="text-xs bg-white px-2 py-0.5 rounded border border-slate-200 hover:border-amber-400 hover:bg-amber-50 transition-colors cursor-pointer text-slate-600"
                        data-testid={`var-${v.replace(/[{}]/g, '')}`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Pane - Live Preview */}
              <div className="flex flex-col min-h-0">
                <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200 flex-shrink-0">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                    <Eye className="w-3.5 h-3.5" />
                    Live Preview
                  </span>
                  <span className="text-xs text-slate-400">Sample data applied</span>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto bg-slate-100 p-4">
                  {/* Email envelope */}
                  <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden" data-testid="preview-envelope">
                    <div className="bg-slate-50 px-5 py-3 border-b border-slate-200 space-y-1.5">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-medium text-slate-500 w-14">From:</span>
                        <span className="text-slate-700">Helios Electrical & Solar</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-medium text-slate-500 w-14">To:</span>
                        <span className="text-slate-700">{SAMPLE_CUSTOMER.name} &lt;{SAMPLE_CUSTOMER.email}&gt;</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="font-medium text-slate-500 w-14">Subject:</span>
                        <span className="font-medium text-slate-900">{replaceVariables(formData.subject) || '(No subject)'}</span>
                      </div>
                    </div>
                    <div className="p-6 min-h-[200px]" data-testid="preview-body">
                      {formData.body_html ? (
                        <div
                          className="prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: replaceVariables(resolveBodyHtml(formData.body_html, templateEditorMode)) }}
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                          <Mail className="w-10 h-10 mb-3 opacity-40" />
                          <p className="text-sm">Start typing HTML in the editor to see a live preview</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between flex-shrink-0 bg-white">
              <p className="text-xs text-slate-400">
                Paste HTML from Canva, Mailchimp, or any email builder. Variables like {'{{first_name}}'} auto-replace with customer data.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditDialog({ open: false, template: null })} data-testid="cancel-template">
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="save-template"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Template
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Email Preview Dialog */}
        <Dialog open={previewDialog.open} onOpenChange={(open) => setPreviewDialog({ open, template: null })}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Email Preview
              </DialogTitle>
              <DialogDescription>
                Preview for: {STATUS_DISPLAY_NAMES[previewDialog.template?.trigger] || previewDialog.template?.trigger}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Sample Data Info */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  This preview uses sample customer data. Actual emails will contain real customer information.
                </p>
              </div>

              {/* Email Preview Card */}
              <div className="border rounded-lg shadow-sm overflow-hidden">
                {/* Email Header */}
                <div className="bg-slate-50 p-4 border-b space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-500 w-16">From:</span>
                    <span className="text-sm text-slate-700">Helios Electrical & Solar &lt;noreply@helioses.co.uk&gt;</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-500 w-16">To:</span>
                    <span className="text-sm text-slate-700">{SAMPLE_CUSTOMER.name} &lt;{SAMPLE_CUSTOMER.email}&gt;</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-500 w-16">Subject:</span>
                    <span className="text-sm font-medium text-slate-900">{previewDialog.template?.previewSubject || '(No subject)'}</span>
                  </div>
                </div>

                {/* Email Body */}
                <div className="p-6 bg-white min-h-[300px]">
                  {previewDialog.template?.previewBody ? (
                    <div 
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: previewDialog.template.previewBody }}
                    />
                  ) : (
                    <p className="text-slate-400 text-center py-8">No email content</p>
                  )}
                </div>
              </div>

              {/* Sample Data Reference */}
              <div className="p-3 bg-slate-50 rounded-lg border">
                <p className="text-xs font-medium text-slate-600 mb-2">Sample data used:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  {Object.entries(SAMPLE_CUSTOMER).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-1">
                      <code className="text-slate-500">{`{{${key}}}`}</code>
                      <span className="text-slate-400">→</span>
                      <span className="text-slate-700 truncate">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewDialog({ open: false, template: null })}>
                Close
              </Button>
              <Button
                onClick={() => {
                  setPreviewDialog({ open: false, template: null });
                  openEditDialog(previewDialog.template?.trigger);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create New Template Dialog */}
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Email Template</DialogTitle>
              <DialogDescription>
                Create a custom email template that can be used when manually sending emails to customers.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium">Template Name</label>
                <Input
                  placeholder="e.g. Follow Up, Thank You, Custom Reminder..."
                  value={newTemplateData.trigger}
                  onChange={(e) => setNewTemplateData({...newTemplateData, trigger: e.target.value})}
                  data-testid="new-template-name"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Subject Line</label>
                <Input
                  placeholder="Email subject..."
                  value={newTemplateData.subject}
                  onChange={(e) => setNewTemplateData({...newTemplateData, subject: e.target.value})}
                  data-testid="new-template-subject"
                />
                <p className="text-xs text-slate-500 mt-1">You can use variables like {'{{first_name}}'}, {'{{last_name}}'} etc.</p>
              </div>
              <div>
                <label className="text-sm font-medium">Email Body (HTML)</label>
                <Textarea
                  placeholder="Write your email HTML content here..."
                  value={newTemplateData.body_html}
                  onChange={(e) => setNewTemplateData({...newTemplateData, body_html: e.target.value})}
                  rows={12}
                  className="font-mono text-sm"
                  data-testid="new-template-body"
                />
              </div>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs font-medium text-slate-600 mb-2">Available Variables:</p>
                <div className="flex flex-wrap gap-1">
                  {TEMPLATE_VARIABLES.map((v) => (
                    <code key={v} className="text-xs bg-white px-1.5 py-0.5 rounded border border-slate-200 cursor-pointer hover:bg-blue-50"
                      onClick={() => setNewTemplateData({...newTemplateData, body_html: newTemplateData.body_html + v})}
                    >
                      {v}
                    </code>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateTemplate} disabled={saving || !newTemplateData.trigger || !newTemplateData.subject} data-testid="save-new-template-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send Email from Template Dialog */}
        <Dialog open={sendDialog.open} onOpenChange={(open) => !open && setSendDialog({ open: false, template: null })}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                Send Email from Template
              </DialogTitle>
              <DialogDescription>
                Select a customer and edit the email before sending.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium">Select Customer</label>
                <Select value={sendCustomerId} onValueChange={handleCustomerSelectForSend}>
                  <SelectTrigger data-testid="send-template-customer-select">
                    <SelectValue placeholder="Choose a customer..." />
                  </SelectTrigger>
                  <SelectContent>
                    {customersList.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.first_name} {c.last_name} ({c.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {sendCustomerId && (
                <>
                  <div>
                    <label className="text-sm font-medium">Subject</label>
                    <Input
                      value={sendSubject}
                      onChange={(e) => setSendSubject(e.target.value)}
                      data-testid="send-template-subject"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-medium">Email Body</label>
                      <EmailModeToggle
                        mode={sendEditorMode}
                        onChange={setSendEditorMode}
                        body={sendBody}
                        setBody={setSendBody}
                        testIdPrefix="send-template-editor-mode"
                      />
                    </div>
                    <Textarea
                      value={sendBody}
                      onChange={(e) => setSendBody(e.target.value)}
                      rows={10}
                      className={sendEditorMode === 'html' ? 'font-mono text-sm bg-slate-900 text-emerald-300' : 'text-sm'}
                      data-testid="send-template-body"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Preview</label>
                    <div
                      className="border border-slate-200 rounded-lg p-4 bg-white max-h-60 overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: resolveBodyHtml(sendBody, sendEditorMode) }}
                    />
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSendDialog({ open: false, template: null })}>Cancel</Button>
              <Button
                variant="outline"
                onClick={() => setSendPreviewOpen(true)}
                disabled={!sendCustomerId || (!sendSubject.trim() && !sendBody.trim())}
                data-testid="send-template-preview-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                onClick={handleSendTemplateEmail}
                disabled={!sendCustomerId || sendingEmail}
                data-testid="send-template-confirm-btn"
              >
                {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Email
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <EmailPreviewDialog
          open={sendPreviewOpen}
          onOpenChange={setSendPreviewOpen}
          to={(customersList.find((c) => c.id === sendCustomerId) || {}).email}
          subject={sendSubject}
          bodyHtml={resolveBodyHtml(sendBody, sendEditorMode)}
          onConfirmSend={handleSendTemplateEmail}
          sending={sendingEmail}
          recipientLabel={(customersList.find((c) => c.id === sendCustomerId) || {}).first_name || 'the customer'}
        />
      </div>
    </Layout>
  );
};
